import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  FileText,
  Download,
  Printer,
  Calendar,
  User,
  Shield,
  CheckCircle,
  Clock,
  Pill,
  X,
  Sparkles,
  Edit3,
  Mail,
  Send,
  Syringe,
} from "lucide-react";
import PdfImage from "./PdfImage";
import SummaryActionsSkeleton from "./SummaryActionsSkeleton";
import {
  SummarizeConsultationsActions,
  setPatientSummaryForActions,
  emailPatientDocument,
} from "@/dispatchers/patientDispatchers";
import { getUsersProfile } from "@/dispatchers/memberDispatchers";
import { getPharmacyByPharmacyId } from "@/dispatchers/pharmacyServiceDispatchers";
import { getPrescriptionByPrescriptionId } from "../../../../../dispatchers/patientPrescriptionDispatcher";
import {
  createConsultationActionSummaryRequest,
  type ConsultationActionSummaryRequest,
} from "@/lib/consultation/ConsultationActionSummaryRequest";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
import { useTitanApp } from "@/hooks/useTitanApp";

interface PharmacistAction {
  id: string;
  type:
    | "prescription"
    | "appointment"
    | "vaccine"
    | "referral"
    | "payment"
    | "vitals"
    | "document"
    | "consultation"
    | "vaccination"
    | "health-check"
    | "counseling"
    | "signature"
    | "imagedocument";
  description: string;
  timestamp: string;
  details?: string;
  status: "completed" | "pending";
  data?: any;
  actionId: string;
  renderedDetails?: string | string[];
}

interface SignatureData {
  signature: string;
  patientName: string;
  signerName: string;
  signerRelationship: string;
  consentText: string;
  timestamp: string;
}

interface SummaryDocumentCustomProps {
  isOpen: boolean;
  onClose: () => void;
  patientName: string;
  patientNHS: string;
  patientEmail?: string;
  pharmacistName: string;
  actions: PharmacistAction[];
  isDarkMode?: boolean;
  onDocumentGenerated?: (documentData: any) => void;
  onEmailSent?: () => void;
  openInPreview?: boolean;
  patientAIChatMessages?: any[];
  selectedPatientDetails?: any;
  consultationId?: string;
  consultationCompletedActions?: any;
  userProfiles?: any;
  patientSummaryForActions?: string;
  summaryDocumentId?: string;
  pharmacyContactDetails?: any;
  currentPharmacy?: any;
  patientConsentSignature?: string;
  documentById?: any;
  isSummaryLetterLoading?: boolean;
  patientReferalLetter?: any;
  showPatientConsultationsLoader?: boolean;
  isDocumentLoading?: boolean;
  isPatientSignatureLoading?: boolean;
  patientDocumentsConultaion?: any;
}

export function SummaryDocumentCustom({
  isOpen,
  onClose,
  patientName,
  selectedPatientDetails,
  summaryDocumentId,
  consultationId,
  consultationCompletedActions,
  userProfiles,
  patientNHS,
  patientEmail,
  pharmacistName,
  actions,
  isDarkMode = false,
  onDocumentGenerated,
  onEmailSent,
  openInPreview = false,
  patientSummaryForActions,
  pharmacyContactDetails,
  currentPharmacy,
  patientConsentSignature,
  documentById,
  isSummaryLetterLoading,
  patientReferalLetter,
  showPatientConsultationsLoader,
  isDocumentLoading,
  isPatientSignatureLoading,
  patientDocumentsConultaion,
}: SummaryDocumentCustomProps) {
  const { appState } = useTitanApp();

  const { currentPharmacyId } = appState;
  const [pdfImageCache, setPdfImageCache] = useState<Record<string, string[]>>(
    {},
  );
  const isSummaryActionLoading =
    showPatientConsultationsLoader ||
    isPatientSignatureLoading ||
    isSummaryLetterLoading;

  const { toast } = useToast();
  // Transform consultationCompletedActions to match the actions format
  const transformedActions = React.useMemo(() => {
    // If consultationCompletedActions is provided, use it (even if empty after transformation)
    if (
      consultationCompletedActions &&
      Array.isArray(consultationCompletedActions)
    ) {
      const transformed = consultationCompletedActions
        .map((action, index) => {
          try {
            // Validate actionID before processing - this is the unique identifier from the API
            if (!action.actionID || typeof action.actionID !== "string") {
              console.warn(
                `Skipping action at index ${index} without valid actionID:`,
                action,
              );
              return null;
            }

            const actionIdString = action.actionID.trim();
            if (actionIdString.length === 0) {
              console.warn(
                `Skipping action at index ${index} with empty actionID`,
              );
              return null;
            }

            // Parse the payload if it's a string
            let payloadData: any = {};
            if (typeof action.payload === "string") {
              try {
                payloadData = JSON.parse(action.payload);
              } catch (parseError) {
                console.warn(
                  `Failed to parse payload for action ${actionIdString}:`,
                  parseError,
                );
                payloadData = {};
              }
            } else {
              payloadData = action.payload || {};
            }

            // Parse nested payload if it exists
            let innerPayload: any = {};
            if (
              payloadData.payload &&
              typeof payloadData.payload === "string"
            ) {
              try {
                innerPayload = JSON.parse(payloadData.payload);
              } catch (e) {
                console.warn(
                  `Failed to parse nested payload for action ${actionIdString}:`,
                  e,
                );
                innerPayload = payloadData.payload;
              }
            } else {
              innerPayload = payloadData.payload || {};
            }

            // Determine action type based on actionName
            const getActionType = (actionName: string): string => {
              const nameMap: { [key: string]: string } = {
                SendReferralLetter: "referral",
                Vaccinations: "vaccine",
                RecordVitals: "vitals",
                AddPrescription: "prescription",
                PrescribeMedication: "prescription",
                RecordSignature: "signature",
                RecordConsent: "signature",
                RecordPayment: "payment",
                BookAppointment: "appointment",
                LibraryDocument: "document",
                CreateTreatmentPlan: "treatment-plan",
              };
              return nameMap[actionName] || actionName.toLowerCase();
            };

            // Extract relevant data from payload
            const data: any = {};

            if (innerPayload?.letterContent) {
              data.title = innerPayload.letterContent.title;
              data.content = innerPayload.letterContent.content;
            }
            // Try to find vitals and vaccination details in either payload level
            const vitalsDetails =
              innerPayload?.vitalsDetails || payloadData?.vitalsDetails;
            const vaccinationDetails =
              innerPayload?.vaccinationDetails ||
              payloadData?.vaccinationDetails;

            if (vitalsDetails) {
              const vitals = vitalsDetails;
              const timestamp = action.timestamp || new Date().toISOString();
              const formattedTime = new Date(timestamp).toLocaleTimeString(
                "en-GB",
                {
                  hour: "2-digit",
                  minute: "2-digit",
                },
              );

              if (vitals.heartRate) {
                const hr = vitals.heartRate;
                data.title = `Heart Rate: ${hr.Value || hr.value || hr} ${
                  hr.Unit || hr.unit || "BPM"
                }`;
                data.content = `${
                  hr.Value || hr.value || hr
                } BPM • Recorded at: ${formattedTime}`;
              } else if (vitals.bloodPressure) {
                const bp = vitals.bloodPressure;
                const sys =
                  bp.Systolic?.Value || bp.systolic?.value || bp.systolic;
                const dia =
                  bp.Diastolic?.Value || bp.diastolic?.value || bp.diastolic;
                const unit =
                  bp.Systolic?.Unit || bp.systolic?.unit || bp.unit || "mmHg";
                data.title = `Blood Pressure: ${sys}/${dia} ${unit}`;
                data.content = `Recorded at: ${formattedTime}`;
              } else if (vitals.weight) {
                const hr = vitals.weight;
                data.title = `Weight: ${hr.Value || hr.value || hr} ${
                  hr.Unit || hr.unit || "kg"
                }`;
                data.content = `${hr.Value || hr.value || hr} ${
                  hr.unit
                }  • Recorded at: ${formattedTime}`;
              } else if (vitals.temperature) {
                const hr = vitals.temperature;
                data.title = `Temperature: ${hr.Value || hr.value || hr} ${
                  hr.Unit || hr.unit || "°C"
                }`;
                data.content = `${hr.Value || hr.value || hr} ${
                  hr.unit
                }  • Recorded at: ${formattedTime}`;
              } else if (vitals.respiratoryRate) {
                const hr = vitals.respiratoryRate;
                data.title = `Respiratory Rate: ${hr.Value || hr.value || hr} ${
                  hr.Unit || hr.unit || "breaths/min"
                }`;
                data.content = `${hr.Value || hr.value || hr} ${
                  hr.Unit || hr.unit || "breaths/min"
                }  • Recorded at: ${formattedTime}`;
              } else if (vitals.height) {
                const hr = vitals.height;
                data.title = `Height: ${hr.Value || hr.value || hr} ${
                  hr.Unit || hr.unit || "cm"
                }`;
                data.content = `${hr.Value || hr.value || hr} ${
                  hr.unit
                }  • Recorded at: ${formattedTime}`;
              }
              data.vitalsData = vitalsDetails;
            }

            const prescriptionDetails =
              innerPayload?.prescriptionDetails ||
              payloadData?.prescriptionDetails;
            if (prescriptionDetails) {
              data.prescriptionData = prescriptionDetails;
            }

            if (vaccinationDetails) {
              const vaccs = vaccinationDetails.vaccinations;

              const pharmacyId = vaccs[0]?.value?.pharmacyId;
              if (pharmacyId) {
                getPharmacyByPharmacyId(pharmacyId);
              }
              if (Array.isArray(vaccs) && vaccs.length > 0) {
                const firstVacc = vaccs[0];
                const timestamp = action.timestamp || new Date().toISOString();
                const formattedTime = new Date(timestamp).toLocaleTimeString(
                  "en-GB",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                );

                data.title = "Vaccination Certificate Generated";
                data.content = `Vaccine: ${
                  firstVacc.value?.vaccineName || ""
                } • Dose: ${firstVacc.dose || ""} • Batch: ${
                  firstVacc.batchNumber || ""
                } • Site: ${
                  firstVacc.site || ""
                } • Generated: ${formattedTime}`;
              }
              // Preserve raw data for certificate rendering
              data.vaccineData = vaccinationDetails;
            }

            // Get image or document from payload data (can be in payloadData.image, payloadData.fileContent or extracted from innerPayload)
            let imageData = null;
            if (action.actionName === "LibraryDocument") {
              imageData = action.payload;
              data.pdfContent = action.payload;
              data.title = "Library Document";
            } else if (payloadData.fileContent) {
              imageData = payloadData.fileContent;
            } else if (payloadData.image) {
              imageData = payloadData.image;
            } else if (innerPayload?.image) {
              imageData = innerPayload.image;
            } else if (innerPayload?.fileContent) {
              imageData = innerPayload.fileContent;
            } else if (innerPayload?.prescriptionDetails?.base64Image) {
              imageData = innerPayload.prescriptionDetails.base64Image;
            } else if (payloadData?.prescriptionDetails?.base64Image) {
              imageData = payloadData.prescriptionDetails.base64Image;
            }

            // Get description based on actionName and data
            const getDescription = (
              actionName: string,
              actionData: any,
            ): string => {
              if (actionData.title) return actionData.title;

              const descMap: { [key: string]: string } = {
                SendReferralLetter: "Letter generated for patient",
                Vaccinations: "Vaccine administered to patient",
                RecordVitals: "Vitals recorded for patient",
                AddPrescription: "Prescription completed for patient",
                PrescribeMedication: "Prescription completed for patient",
                RecordSignature: "Patient Consent Signature Captured",
                RecordConsent: "Patient Consent Signature Captured",
                RecordPayment: "Payment processed for patient",
                BookAppointment: "Appointment booked for patient",
              };
              return descMap[actionName] || `${actionName} completed`;
            };

            // Generate timestamp using action's timestamp if available
            const timestamp = action.timestamp || new Date().toISOString();

            return {
              id: actionIdString,
              type: getActionType(action.actionName),
              description: getDescription(action.actionName, data),
              timestamp: timestamp,
              status: action.isPerformed ? "completed" : "pending",
              data: Object.keys(data).length > 0 ? data : undefined,
              actionId: actionIdString,
              details: imageData || data.content,
              actionDetails: payloadData || "",
            };
          } catch (error) {
            console.error(
              `Error transforming action at index ${index}:`,
              action,
              error,
            );
            return null;
          }
        })
        .filter((action) => action !== null)
        // Exclude draft prescription/vaccine actions from the summary document
        // These drafts (CreatePrescriptionDraft, CreateVaccineDraft) are only
        // shown in the Actions panel sidebar, not in the summary document.
        .filter((action) => {
          const originalAction = consultationCompletedActions?.find(
            (a: any) => a.actionID === action?.actionId,
          );
          if (!originalAction) return true;
          const actionName = originalAction.actionName;
          if (
            actionName === "CreatePrescriptionDraft" ||
            actionName === "CreateVaccineDraft"
          ) {
            return false;
          }
          // Also exclude if draftStatus is pending in the payload
          try {
            const rawPayload =
              typeof originalAction.payload === "string"
                ? JSON.parse(originalAction.payload)
                : originalAction.payload || {};
            if (rawPayload?.draftStatus === "pending") return false;
          } catch {
            // ignore parse errors
          }
          return true;
        }) as PharmacistAction[];

      // Group all vitals into a single consolidated action
      const finalTransformed: PharmacistAction[] = [];
      const vitalsActions = transformed.filter((a) => a.type === "vitals");
      const referralActions = transformed.filter((a) => a.type === "referral");
      const otherActions = transformed.filter(
        (a) =>
          a.type !== "vitals" && a.type !== "referral" && a.type !== "document",
      );

      if (vitalsActions.length > 0) {
        const combinedVitalsData = vitalsActions.reduce((acc, curr) => {
          return { ...acc, ...curr.data?.vitalsData };
        }, {});

        finalTransformed.push({
          ...vitalsActions[0],
          id: "vitals-consolidated",
          actionId: "vitals-consolidated",
          description: "Vitals Record",
          data: {
            ...vitalsActions[0].data,
            vitalsData: combinedVitalsData,
            isGroup: true,
            containerActionIds: vitalsActions.map((a) => a.actionId),
          },
          details: "Multiple vitals recorded",
        });
      }

      const capitalize = (str: string) =>
        str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

      // if (referralActions.length > 0) {
      //   // console.log("doctype" ,referralActions);
      //   // console.log(referralActions.actionDetails.documentType.type);s
      //   finalTransformed.push({
      //     ...referralActions[0],
      //     id: "referral-consolidated",
      //     actionId: "referral-consolidated",
      //     description: "Document Template",
      //     data: {
      //       ...referralActions[0].data,
      //       isGroup: true,
      //       containerActionIds: referralActions.map((a) => a.actionId),
      //     },
      //     details: "Referral letter(s) generated",
      //   });
      // }

      if (
        patientDocumentsConultaion &&
        Array.isArray(patientDocumentsConultaion) &&
        patientDocumentsConultaion.length > 0
      ) {
        // Filter out auto-generated vaccination documents to avoid duplication
        const validDocuments = patientDocumentsConultaion.filter((doc) => {
          const title = (
            doc.title ||
            doc.name ||
            doc.fileName ||
            ""
          ).toLowerCase();
          const desc = (doc.description || "").toLowerCase();

          // Exclude vaccination and prescription documents to avoid duplication
          const isRedundantDoc =
            desc.includes("vaccination") || desc.includes("prescription");

          // Include all other documents (GP letters, etc.) if they have content
          return !isRedundantDoc && doc.fileContent;
        });

        if (validDocuments.length > 0) {
          finalTransformed.push({
            id: "consultation-documents-consolidated",
            actionId: "consultation-documents-consolidated",
            type: "document",
            description: "Documents uploaded",
            timestamp: validDocuments[0].date || new Date().toISOString(),
            status: "completed",
            data: {
              documents: validDocuments,
              isGroup: true,
              containerActionIds: ["consultation-documents-consolidated"],
            },
            details: `${validDocuments.length} document(s) uploaded`,
          } as PharmacistAction);
        }
      }

      return [...otherActions, ...finalTransformed];
    }

    // Only use actions prop if consultationCompletedActions is not provided at all
    return actions || [];
  }, [consultationCompletedActions, patientDocumentsConultaion]);
  // Use transformed actions directly - they are already derived from consultationCompletedActions or actions prop
  const effectiveActions = transformedActions;

  // Check if details contain base64 image data
  const isBase64ImageData = (details) => {
    if (typeof details === "string") {
      return (
        details.includes("data:image") ||
        details.startsWith("/9j/") ||
        details.startsWith("iVBORw0KGgo")
      );
    }
    return false;
  };
  const isBase64Pdf = (data) =>
    typeof data === "string" &&
    (data.startsWith("data:application/pdf") || data.startsWith("JVBERi0"));

  // Check if details contain base64 PDF data
  const isBase64PdfData = (details: any) => {
    if (typeof details === "string") {
      return (
        details.includes("application/pdf") || details.startsWith("JVBERi")
      );
    }
    return false;
  };

  const isBase64TextData = (details: any) => {
    if (typeof details === "string") {
      return (
        details.includes("data:text/plain") ||
        details.includes("data:application/octet-stream") ||
        details.startsWith("LSBD") ||
        (details.length > 20 &&
          !details.includes("data:") &&
          !details.startsWith("/9j/") &&
          !details.startsWith("iVBORw0KGgo") &&
          !details.startsWith("JVBERi") &&
          /^[A-Za-z0-9+/=]+$/.test(details.trim()))
      );
    }
    return false;
  };

  const decodeBase64Text = (base64: string) => {
    try {
      const base64Content = base64.includes(",")
        ? base64.split(",")[1]
        : base64;
      return atob(base64Content);
    } catch (e) {
      console.error("Failed to decode base64 text:", e);
      return "Failed to decode content";
    }
  };

  const getImageUrl = (details: string) => {
    if (!details) return "";
    if (details.startsWith("data:image")) return details;
    if (details.startsWith("/9j/")) return `data:image/jpeg;base64,${details}`;
    if (details.startsWith("iVBORw0KGgo"))
      return `data:image/png;base64,${details}`;
    return `data:image/png;base64,${details}`;
  };
  const [actionsWithPrescriptionImages, setActionsWithPrescriptionImages] =
    useState<PharmacistAction[]>([]);

  React.useEffect(() => {
    const fetchAndSetPrescriptionImages = async () => {
      const completed = effectiveActions.filter(
        (action) => action.status === "completed",
      );

      if (!completed || completed.length === 0) return;

      const updatedActions = [...completed];
      let hasUpdates = false;

      for (let i = 0; i < updatedActions.length; i++) {
        const action = updatedActions[i];

        if (
          action.type === "prescription" &&
          action.data?.prescriptionData?.prescriptionId
        ) {
          const prescriptionId = action.data.prescriptionData.prescriptionId;

          if (
            !action.details ||
            typeof action.details !== "string" ||
            (!action.details.startsWith("data:image") &&
              !action.details.startsWith("/9j/") &&
              !action.details.startsWith("iVBORw0KGgo"))
          ) {
            try {
              const result =
                await getPrescriptionByPrescriptionId(prescriptionId);

              if (result?.base64Image) {
                updatedActions[i] = {
                  ...action,
                  details: result.base64Image,
                  data: {
                    ...action.data,
                    prescriptionData: {
                      ...action.data.prescriptionData,
                      base64Image: result.base64Image,
                    },
                  },
                };
                hasUpdates = true;
              }
            } catch (error) {
              console.error(
                `Error fetching prescription image for ID ${prescriptionId}:`,
                error,
              );
            }
          }
        }
      }

      if (hasUpdates) {
        setActionsWithPrescriptionImages(updatedActions);
      }
    };

    fetchAndSetPrescriptionImages();
  }, [effectiveActions]);

  const completedActions =
    actionsWithPrescriptionImages.length > 0
      ? actionsWithPrescriptionImages
      : effectiveActions.filter((action) => action.status === "completed");

  const signatureAction = completedActions?.find(
    (action) => action.type === "signature",
  );
  const displayedActions = completedActions?.filter(
    (action) => action.type !== "signature" && action.type !== "imagedocument",
  );
  const extractedSignatureData = signatureAction?.actionDetails
    ?.patientRelationStatus
    ? {
        signature: patientConsentSignature?.patientSignature || "",
        patientName:
          signatureAction.actionDetails?.patientRelationStatus?.name ||
          patientName,
        signerName:
          signatureAction.actionDetails?.patientRelationStatus?.name || "",
        signerRelationship:
          signatureAction.actionDetails?.patientRelationStatus?.relationship ||
          "patient",
        consentText:
          signatureAction.actionDetails?.consentStatement ||
          "I consent to the clinical services provided.",
        timestamp: signatureAction.timestamp,
      }
    : null;
  // Use extracted signature data if available, otherwise use passed prop
  const effectivePatientSignature = extractedSignatureData || "";

  // Extract pharmacist signature from userProfiles if available
  const extractedPharmacistSignature = userProfiles?.digitalSignatures
    ?.signature
    ? {
        signature: userProfiles.digitalSignatures.signature,
        patientName:
          userProfiles?.digitalSignatures?.userCertificate?.commonName ||
          pharmacistName ||
          "",
        signerName:
          userProfiles?.digitalSignatures?.userCertificate?.commonName ||
          pharmacistName ||
          "",
        signerRelationship: "",
        consentText: "Digital signature verified and captured.",
        timestamp: new Date().toISOString(),
      }
    : null;

  // Use extracted signature data if available, otherwise use passed prop
  const effectivePharmacistSignature = extractedPharmacistSignature || "";

  const [selectedActionIds, setSelectedActionIds] = useState<string[]>([]);
  const [localSummary, setLocalSummary] = useState<string>(
    patientSummaryForActions || "",
  );
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showAiSection, setShowAiSection] = useState(true);
  const [isAiSummarySelected, setIsAiSummarySelected] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isDocumentGenerated, setIsDocumentGenerated] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);

  const consultationDate = new Date().toLocaleDateString("en-GB");
  const consultationTime = new Date().toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Sync patientSummaryForActions prop with local state
  React.useEffect(() => {
    if (patientSummaryForActions) {
      setLocalSummary(patientSummaryForActions);
    } else {
      setLocalSummary("");
    }
  }, [patientSummaryForActions]);

  // Open in preview mode if openInPreview is true
  React.useEffect(() => {
    if (isOpen && openInPreview) {
      setShowPreview(true);
      setIsDocumentGenerated(true);
    }
  }, [isOpen, openInPreview]);

  React.useEffect(() => {
    getUsersProfile();
  }, []);

  // Initialize email dialog when opened
  // Reset document generated state when preview closes
  React.useEffect(() => {
    if (!showPreview) {
      setIsDocumentGenerated(false);
    }
  }, [showPreview]);

  React.useEffect(() => {
    if (showEmailDialog) {
      setEmailRecipient(patientEmail || "");
      setEmailSubject(`Consultation Summary - ${patientName}`);
      setEmailMessage(
        `Dear Recipient,\n\nPlease find attached the following documents for your records:\n\nIf you have any questions, please don't hesitate to contact us.\n\nBest regards,\n${pharmacistName}`,
      );
    }
  }, [
    showEmailDialog,
    patientEmail,
    patientName,
    consultationDate,
    pharmacistName,
  ]);

  const handleGenerateDocument = () => {
    setShowPreview(true);
  };

  const pdfBase64ToImage = async (pdfBase64: string): Promise<string[]> => {
    const base64 = pdfBase64.replace("data:application/pdf;base64,", "");
    const base64Content = base64.includes(",") ? base64.split(",")[1] : base64;
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    const pages: string[] = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1.2 });
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: ctx,
        viewport: viewport,
        canvas: canvas,
      }).promise;

      pages.push(canvas.toDataURL("image/png"));
    }

    return pages;
  };
  const textBase64ToImage = async (textBase64: string): Promise<string> => {
    try {
      // Decode the base64 text
      const decodedText = atob(textBase64);

      // Create a canvas to render text as image
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas context not available");

      // Set canvas dimensions
      const padding = 20;
      const lineHeight = 16;
      const maxWidth = 800;
      const charWidth = 8;

      // Calculate number of lines needed
      const lines = decodedText.split("\n");
      const wrappedLines: string[] = [];

      for (const line of lines) {
        if (line.length === 0) {
          wrappedLines.push("");
        } else {
          // Wrap long lines
          let currentLine = "";
          for (const char of line) {
            currentLine += char;
            if (currentLine.length * charWidth > maxWidth - padding * 2) {
              wrappedLines.push(currentLine);
              currentLine = "";
            }
          }
          if (currentLine) {
            wrappedLines.push(currentLine);
          }
        }
      }

      canvas.width = maxWidth;
      canvas.height = wrappedLines.length * lineHeight + padding * 2 + 10;

      // Set background
      ctx.fillStyle = "#f5f5f5";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw border
      ctx.strokeStyle = "#ddd";
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, canvas.width, canvas.height);

      // Draw text
      ctx.fillStyle = "#333";
      ctx.font = "12px monospace";
      ctx.textBaseline = "top";

      wrappedLines.forEach((line, index) => {
        ctx.fillText(line || "", padding, padding + index * lineHeight);
      });

      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error converting text to image:", error);
      return "";
    }
  };

  const handleGenerateAndSaveDocument = async () => {
    setIsGeneratingDocument(true);

    try {
      const { addDocument, getDocumentTypeEnum } =
        await import("@/lib/dashboard/patientDocumentsData");
      const html2canvas = (await import("html2canvas")).default;
      const jsPDF = (await import("jspdf")).jsPDF;
      // Generate PDF content as HTML
      const rawSelectedActions = displayedActions.filter((a) =>
        a.id === "vitals-consolidated" ||
        a.id === "referral-consolidated" ||
        a.id === "consultation-documents-consolidated"
          ? a.data?.containerActionIds?.some((id) =>
              selectedActionIds.includes(id),
            )
          : selectedActionIds.includes(a.actionId),
      );

      const selectedActions = await Promise.all(
        rawSelectedActions.map(async (action) => {
          if (
            action.id === "referral-consolidated" &&
            patientReferalLetter?.referalLetter
          ) {
            const imageBase64s = [];
            const referralLetters = Array.isArray(
              patientReferalLetter.referalLetter,
            )
              ? patientReferalLetter.referalLetter
              : [patientReferalLetter.referalLetter];

            for (const pdf of referralLetters) {
              if (isBase64Pdf(pdf)) {
                const images = await pdfBase64ToImage(pdf);
                imageBase64s.push(...images);
              }
            }
            return { ...action, renderedDetails: imageBase64s };
          }

          if (
            action.id === "consultation-documents-consolidated" &&
            action.data?.documents
          ) {
            const imageBase64s = [];
            for (const doc of action.data.documents) {
              if (doc.fileContent) {
                // Check if it's a PDF
                if (isBase64Pdf(doc.fileContent)) {
                  const images = await pdfBase64ToImage(doc.fileContent);
                  imageBase64s.push(...images);
                }
                // Check if it's an image file
                else if (
                  doc.fileType &&
                  /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(doc.fileType)
                ) {
                  const imageUrl = `data:image/${doc.fileType?.replace(".", "")};base64,${doc.fileContent}`;
                  imageBase64s.push(imageUrl);
                }
                // For text files, convert to image
                else if (
                  doc.fileType &&
                  /\.(txt|csv|log|json|xml)$/i.test(doc.fileType)
                ) {
                  try {
                    const textImage = await textBase64ToImage(doc.fileContent);
                    if (textImage) {
                      imageBase64s.push(textImage);
                    }
                  } catch (e) {
                    // Skip if conversion fails
                  }
                }
              }
            }
            return { ...action, renderedDetails: imageBase64s };
          }

          const pdfContent = action.data?.pdfContent || action.details;
          if (isBase64Pdf(pdfContent)) {
            const imageBase64s = await pdfBase64ToImage(pdfContent);
            return { ...action, renderedDetails: imageBase64s };
          }

          if (isBase64ImageData(action.details)) {
            return { ...action, renderedDetails: action.details };
          }

          return { ...action, renderedDetails: null };
        }),
      );

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Pharmacy Consultation Summary - ${patientName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; color: #333; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .patient-info { background: #f5f5f5; padding: 15px; margin: 10px 0; border-radius: 5px; }
            .action-item { margin: 10px 0; padding: 10px; border-left: 3px solid #0066cc; background: #f9f9f9; }
            .summary-section { margin: 20px 0; padding: 15px; background: #f0f8ff; border-radius: 5px; white-space: pre-wrap; }
            .signature-section { margin: 20px 0; padding: 15px; border: 1px solid #ddd; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; }
            h1 { margin: 0; }
            h2 { margin-top: 20px; margin-bottom: 10px; }
            p { margin: 5px 0; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f0f0f0; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Pharmacy Consultation Summary</h1>
            <p><strong>Date:</strong> ${consultationDate} ${consultationTime}</p>
          </div>

          <div class="patient-info">
            <h2>Patient Information</h2>
            <p><strong>Name:</strong> ${patientName}</p>
            <p><strong>NHS Number:</strong> ${patientNHS}</p>
            <p><strong>Pharmacist:</strong> ${pharmacistName}</p>
          </div>

          ${
            isAiSummarySelected && localSummary
              ? `
            <div class="summary-section">
              <h2>Consultation Summary</h2>
              ${localSummary.replace(/</g, "&lt;").replace(/>/g, "&gt;")}
            </div>
          `
              : ""
          }
          
          <div>
            <h2>Actions Completed (${selectedActions.length})</h2>
            ${selectedActions
              .map((action) => {
                if (action.type === "vaccine" && action.data?.vaccineData) {
                  const vaccs = action.data.vaccineData.vaccinations || [];
                  return `
                  <div class="action-item" style="border: 2px solid #0066cc; border-radius: 8px; padding: 20px; background: white;">
                    <div style="text-align: center; border-bottom: 2px solid #0066cc; margin-bottom: 20px; padding-bottom: 10px;">
                      <h2 style="color: #0066cc; margin: 0; font-size: 24px; text-transform: uppercase;">${
                        currentPharmacy?.teamPharmacyName || ""
                      }</h2>
                      <p style="margin: 5px 0; font-size: 14px;">${(currentPharmacy?.teamPharmacyAddress || "").replace(/\n/g, "<br>")}</p>
                <p style="margin: 5px 0; font-size: 14px;">Email: ${
                  pharmacyContactDetails?.emailAddress || ""
                }</p>
                      <p style="margin: 5px 0; font-size: 14px;">Tel: ${
                        pharmacyContactDetails?.phoneNumber || ""
                      }</p>
                    </div>
                    <h3 style="text-align: center; text-decoration: underline; margin-bottom: 20px;">Vaccination Certificate</h3>
                    <p><strong>Patient Name:</strong> ${patientName}</p>
                    <p><strong>NHS Number:</strong> ${patientNHS}</p>
                    <p style="margin-bottom: 20px;">This certificate certifies that the above patient has had the following vaccinations:</p>
                    <table style="width: 100%; border-collapse: collapse;">
                      <thead>
                        <tr style="background: #f0f8ff;">
                          <th style="border: 1px solid #0066cc; padding: 8px;">Vaccination</th>
                          <th style="border: 1px solid #0066cc; padding: 8px;">Date Administered</th>
                          <th style="border: 1px solid #0066cc; padding: 8px;">Vaccine Site</th>
                          <th style="border: 1px solid #0066cc; padding: 8px;">Batch Number</th>
                          <th style="border: 1px solid #0066cc; padding: 8px;">Expiry</th>
                          <th style="border: 1px solid #0066cc; padding: 8px;">Next Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${vaccs
                          .map(
                            (v) => `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              v.vaccineName || ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${consultationDate}</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              v.site || ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              v.batchNumber || ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              v.expiryDate || ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;"></td>
                          </tr>
                        `,
                          )
                          .join("")}
                      </tbody>
                    </table>
                    <div style="margin-top: 20px;">
                      <p><strong>Vaccination administered by:</strong></p>
                      <p>${pharmacistName}</p>
                    </div>
                  </div>
                `;
                }

                if (
                  (action.type === "vitals" ||
                    action.id === "vitals-consolidated") &&
                  action.data?.vitalsData
                ) {
                  const vitals = action.data.vitalsData;
                  return `
                  <div class="action-item" style="border: 2px solid #0066cc; border-radius: 8px; padding: 20px; background: white; margin-bottom: 20px;">
                    <h3 style="text-align: center; text-decoration: underline; margin-bottom: 20px;">Vitals Record</h3>
                    <p><strong>Patient Name:</strong> ${patientName}</p>
                    <p><strong>NHS Number:</strong> ${patientNHS}</p>
                    <p style="margin-bottom: 20px;">The following vitals were recorded during the consultation:</p>
                    <table style="width: 100%; border-collapse: collapse;">
                      <thead>
                        <tr style="background: #f0f8ff;">
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Vital</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Value</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${
                          vitals.heartRate
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Heart Rate</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.heartRate.Value ||
                              vitals.heartRate.value ||
                              vitals.heartRate
                            } ${
                              vitals.heartRate.Unit ||
                              vitals.heartRate.unit ||
                              "BPM"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(
                              vitals.heartRate.recordedAt,
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.weight
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Weight</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.weight.Value ||
                              vitals.weight.value ||
                              vitals.weight
                            } ${
                              vitals.weight.Unit || vitals.weight.unit || "kg"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(vitals.weight.recordedAt).toLocaleString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.height
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Height</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.height.Value ||
                              vitals.height.value ||
                              vitals.height
                            } ${
                              vitals.height.Unit || vitals.height.unit || "cm"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(vitals.height.recordedAt).toLocaleString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.temperature
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Temperature</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.temperature.Value ||
                              vitals.temperature.value ||
                              vitals.temperature
                            } ${
                              vitals.temperature.Unit ||
                              vitals.temperature.unit ||
                              "°C"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(
                              vitals.temperature.recordedAt,
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.respiratoryRate
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Respiratory Rate</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.respiratoryRate.Value ||
                              vitals.respiratoryRate.value ||
                              vitals.respiratoryRate
                            } ${
                              vitals.respiratoryRate.Unit ||
                              vitals.respiratoryRate.unit ||
                              "breaths/min"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(
                              vitals.respiratoryRate.recordedAt,
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.bloodPressure
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Blood Pressure</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                              ${
                                vitals.bloodPressure.Systolic?.Value ||
                                vitals.bloodPressure.systolic?.value ||
                                vitals.bloodPressure.systolic
                              }/${
                                vitals.bloodPressure.Diastolic?.Value ||
                                vitals.bloodPressure.diastolic?.value ||
                                vitals.bloodPressure.diastolic
                              } 
                              ${
                                vitals.bloodPressure.Systolic?.Unit ||
                                vitals.bloodPressure.systolic?.unit ||
                                vitals.bloodPressure.unit ||
                                "mmHg"
                              }
                            </td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(
                              vitals.bloodPressure.recordedAt,
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                                                ${
                                                  vitals.circumference
                                                    ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Circumference</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                              ${
                                vitals.circumference.Value ||
                                vitals.circumference.value ||
                                vitals.circumference
                              } ${
                                vitals.circumference.Unit ||
                                vitals.circumference.unit ||
                                "cm"
                              }
                            </td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.circumference.recordedAt
                                ? new Date(
                                    vitals.circumference.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                                                    : ""
                                                }
                        ${
                          vitals.bmi
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">BMI</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                              ${
                                vitals.bmi.Value ||
                                vitals.bmi.value ||
                                vitals.bmi
                              } ${vitals.bmi.Unit || vitals.bmi.unit || "kg/m²"}
                            </td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(vitals.bmi.recordedAt).toLocaleString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.OxygenSaturation
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Oxygen Saturation</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.OxygenSaturation.Value ||
                              vitals.OxygenSaturation.value ||
                              vitals.OxygenSaturation
                            } ${
                              vitals.OxygenSaturation.Unit ||
                              vitals.OxygenSaturation.unit ||
                              "%"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.OxygenSaturation.recordedAt
                                ? new Date(
                                    vitals.OxygenSaturation.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.PainScore
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">NEWS Pain Score</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.PainScore.Value ||
                              vitals.PainScore.value ||
                              vitals.PainScore
                            } ${
                              vitals.PainScore.Unit ||
                              vitals.PainScore.unit ||
                              ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.PainScore.recordedAt
                                ? new Date(
                                    vitals.PainScore.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.GlucoseLevel
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Blood Sugar</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.GlucoseLevel.Value ||
                              vitals.GlucoseLevel.value ||
                              vitals.GlucoseLevel
                            } ${
                              vitals.GlucoseLevel.Unit ||
                              vitals.GlucoseLevel.unit ||
                              "mmol/L"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.GlucoseLevel.recordedAt
                                ? new Date(
                                    vitals.GlucoseLevel.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.Consciousness
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Consciousness (AVPU)</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.Consciousness.Value ||
                              vitals.Consciousness.value ||
                              vitals.Consciousness
                            } ${
                              vitals.Consciousness.Unit ||
                              vitals.Consciousness.unit ||
                              ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.Consciousness.recordedAt
                                ? new Date(
                                    vitals.Consciousness.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.SupplementalOxygen
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Supplemental Oxygen</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.SupplementalOxygen.Value ||
                              vitals.SupplementalOxygen.value ||
                              vitals.SupplementalOxygen
                            } ${
                              vitals.SupplementalOxygen.Unit ||
                              vitals.SupplementalOxygen.unit ||
                              ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.SupplementalOxygen.recordedAt
                                ? new Date(
                                    vitals.SupplementalOxygen.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                      </tbody>
                    </table>
                    <div style="margin-top: 20px;">
                      <p><strong>Vitals recorded by:</strong></p>
                      <p>${pharmacistName}</p>
                    </div>
                  </div>
                `;
                }

                return `
                <div class="action-item">
                  <h3>${
                    action.type.charAt(0).toUpperCase() + action.type.slice(1)
                  }</h3>
                  <p><strong>Description:</strong> ${action.description}</p>
                  ${
                    Array.isArray(action.renderedDetails)
                      ? action.renderedDetails
                          .map((src) => {
                            // Check if it's an HTML string (contains <) or an image URL
                            if (src.startsWith("<")) {
                              return `<div style="margin: 15px 0;">${src}</div>`;
                            } else {
                              return `<div style="margin: 15px 0;"><img src="${src}" style="max-width:100%;height:auto;margin-top:8px;" /></div>`;
                            }
                          })
                          .join("")
                      : action.renderedDetails
                        ? action.renderedDetails.startsWith("<")
                          ? `<div style="margin: 10px 0;">${action.renderedDetails}</div>`
                          : `<img src="${action.renderedDetails}" style="max-width:100%;height:auto;margin-top:8px;" />`
                        : isBase64TextData(action.details)
                          ? `<div style="margin: 10px 0; padding: 15px; border: 1px solid #ddd; background: #fff; white-space: pre-wrap; font-family: monospace; font-size: 11px;">${decodeBase64Text(
                              action.details,
                            )}</div>`
                          : action.details
                            ? `<p><strong>Details:</strong> ${action.details}</p>`
                            : ""
                  }
                  <p><strong>Completed:</strong> ${new Date(
                    action.timestamp,
                  ).toLocaleString("en-GB")}</p>
                </div>
              `;
              })
              .join("")}
          </div>

          ${
            effectivePatientSignature
              ? `
            <div class="signature-section">
              <h3>Patient Consent & Signature</h3>
              <p><strong>Consent:</strong> ${
                effectivePatientSignature.consentText
              }</p>
              <p><strong>Signed by:</strong> ${
                effectivePatientSignature.patientName
              }</p>
              <p><strong>Date & Time:</strong> ${new Date(
                effectivePatientSignature.timestamp,
              ).toLocaleString("en-GB")}</p>
              <img src="${
                effectivePatientSignature.signature
              }" alt="Patient Signature" class="signature-image" />
            </div>
          `
              : ""
          }

          ${
            effectivePharmacistSignature
              ? `
            <div class="signature-section">
              <h3>Pharmacist Signature</h3>
              <p><strong>Pharmacist:</strong> ${
                effectivePharmacistSignature.patientName
              }</p>
              <p><strong>Date & Time:</strong> ${new Date(
                effectivePharmacistSignature.timestamp,
              ).toLocaleString("en-GB")}</p>
              <img src="${
                effectivePharmacistSignature.signature
              }" alt="Pharmacist Signature" class="signature-image" />
            </div>
          `
              : "<p>No pharmacist signature captured</p>"
          }

          <div class="footer">
            <p>This document was generated electronically on ${new Date().toLocaleString(
              "en-GB",
            )}</p>
            <p>© 2025 Pharmacy Management System</p>
          </div>
        </body>
        </html>
      `;
      // ------------------------------------
      // 1️⃣ CREATE TEMP CONTAINER
      // ------------------------------------
      const container = document.createElement("div");
      container.innerHTML = htmlContent; // ⬅️ your existing htmlContent
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.background = "#ffffff";
      document.body.appendChild(container);

      // ------------------------------------
      // 2️⃣ HTML → CANVAS (OPTIMIZED)
      // ------------------------------------
      const canvas = await html2canvas(container, {
        scale: 1.25,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      document.body.removeChild(container);

      // ------------------------------------
      // 3️⃣ CANVAS → MULTI-PAGE PDF (NO DUPLICATION)
      // ------------------------------------
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      const pageHeightPx = Math.floor((canvasWidth * pageHeight) / pageWidth);

      let renderedHeight = 0;
      let pageIndex = 0;

      while (renderedHeight < canvasHeight) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = Math.min(
          pageHeightPx,
          canvasHeight - renderedHeight,
        );

        const ctx = pageCanvas.getContext("2d")!;
        ctx.drawImage(
          canvas,
          0,
          renderedHeight,
          canvasWidth,
          pageCanvas.height,
          0,
          0,
          canvasWidth,
          pageCanvas.height,
        );

        const imgData = pageCanvas.toDataURL("image/jpeg", 0.72);

        if (pageIndex > 0) pdf.addPage();

        const imgWidth = pageWidth - 20;
        const imgHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;

        pdf.addImage(imgData, "JPEG", 10, 10, imgWidth, imgHeight);

        renderedHeight += pageCanvas.height;
        pageIndex++;
      }

      // ------------------------------------
      // 4️⃣ PDF SIZE (ACCURATE)
      // ------------------------------------
      const pdfBlob = pdf.output("blob");
      const fileSizeMB = pdfBlob.size / 1024 / 1024;

      if (fileSizeMB > 5) {
        toast({
          title: "Large File Warning",
          description: `Document size is ${fileSizeMB.toFixed(
            2,
          )}MB. Upload may take longer.`,
        });
      }

      // ------------------------------------
      // 5️⃣ BLOB → BASE64
      // ------------------------------------
      const base64File = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]);
        };
        reader.onerror = () =>
          reject(new Error("Failed to convert PDF to Base64"));
        reader.readAsDataURL(pdfBlob);
      });

      // ------------------------------------
      // 6️⃣ SAVE DOCUMENT
      // ------------------------------------
      const backendDoc = {
        patientId: selectedPatientDetails?.patientID || "",
        title: `Consultation Summary - ${patientName}`,
        description: `Pharmacy consultation summary document generated on ${consultationDate}`,
        size: `${Math.round(pdfBlob.size / 1024)} KB`,
        status: 1,
        fileContent: base64File,
        contentType: "application/pdf",
        type: getDocumentTypeEnum("General"),
      };

      await addDocument(backendDoc);

      setIsGeneratingDocument(false);
      setIsDocumentGenerated(true);

      toast({
        title: "Document Generated",
        description:
          "Consultation summary document has been generated and saved successfully",
      });
    } catch (error) {
      console.error("Error generating document:", error);
      setIsGeneratingDocument(false);

      toast({
        title: "Error",
        description: "Failed to generate and save document. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipient || !emailSubject) {
      toast({
        title: "Missing Information",
        description: "Please fill in recipient and subject fields",
        variant: "destructive",
      });
      return;
    }

    setIsSendingEmail(true);

    try {
      const response = await emailPatientDocument({
        email: emailRecipient,
        message: emailMessage,
        documentIds: [summaryDocumentId],
        pharmacyId: currentPharmacyId,
      });

      if (response) {
        toast({
          title: "Email Sent",
          description: `Consultation summary sent successfully to ${emailRecipient}`,
        });

        // Notify parent that email was sent
        if (onEmailSent) {
          onEmailSent();
        }

        setShowEmailDialog(false);
        // Reset email fields
        setEmailRecipient("");
        setEmailSubject("");
        setEmailMessage("");
      } else {
        throw new Error("Failed to send email");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const toggleAction = (actionId: string) => {
    setSelectedActionIds((prevSelected) => {
      if (prevSelected.includes(actionId)) {
        return prevSelected.filter((id) => id !== actionId);
      } else {
        return [...prevSelected, actionId];
      }
    });
  };

  const selectAll = () => {
    const allActionIds = displayedActions.flatMap((a) =>
      a.id === "vitals-consolidated" && a.data?.containerActionIds
        ? a.data.containerActionIds
        : [a.actionId],
    );
    setSelectedActionIds(allActionIds);
    setIsAiSummarySelected(true);
  };

  const deselectAll = () => {
    setSelectedActionIds([]);
    setIsAiSummarySelected(false);
  };

  const generateAiSummary = async () => {
    if (!consultationId) {
      toast({
        title: "Error",
        description: "Consultation ID is missing.",
        variant: "destructive",
      });
      return;
    }

    if (selectedActionIds.length === 0) {
      toast({
        title: "No Actions Selected",
        description: "Please select at least one action to summarize.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingSummary(true);

    try {
      const payload = createConsultationActionSummaryRequest(
        consultationId,
        selectedActionIds,
      );

      const response = await SummarizeConsultationsActions(payload);
      let summaryText = "";

      if (response?.data) {
        summaryText = response.data;
      } else if (response && typeof response === "string") {
        summaryText = response;
      } else if (response) {
        summaryText = JSON.stringify(response);
      } else {
        throw new Error("Invalid response format");
      }

      setLocalSummary(summaryText);
      setPatientSummaryForActions(summaryText);

      toast({
        title: "Summary Generated",
        description: "AI summary has been generated successfully.",
      });
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to generate summary.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case "prescription":
        return <Pill className="w-4 h-4 text-blue-500" />;
      case "vaccine":
      case "vaccination":
        return <Shield className="w-4 h-4 text-green-500" />;
      case "consultation":
        return <User className="w-4 h-4 text-purple-500" />;
      case "vitals":
      case "health-check":
        return <CheckCircle className="w-4 h-4 text-orange-500" />;
      case "signature":
        return <FileText className="w-4 h-4 text-indigo-500" />;
      case "appointment":
        return <Calendar className="w-4 h-4 text-pink-500" />;
      case "referral":
        return <FileText className="w-4 h-4 text-red-500" />;
      case "document":
        return <FileText className="w-4 h-4 text-teal-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionBadge = (type: string) => {
    const configs = {
      prescription: {
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "Prescription",
      },
      vaccination: {
        color: "bg-green-100 text-green-800 border-green-200",
        label: "Vaccination",
      },
      vaccine: {
        color: "bg-green-100 text-green-800 border-green-200",
        label: "Vaccine",
      },
      consultation: {
        color: "bg-purple-100 text-purple-800 border-purple-200",
        label: "Consultation",
      },
      "health-check": {
        color: "bg-orange-100 text-orange-800 border-orange-200",
        label: "Health Check",
      },
      vitals: {
        color: "bg-orange-100 text-orange-800 border-orange-200",
        label: "Vitals",
      },
      counseling: {
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        label: "Counseling",
      },
      signature: {
        color: "bg-indigo-100 text-indigo-800 border-indigo-200",
        label: "Signature",
      },
      appointment: {
        color: "bg-pink-100 text-pink-800 border-pink-200",
        label: "Appointment",
      },
      referral: {
        color: "",
        label: "Referral",
      },
      document: {
        color: "bg-teal-100 text-teal-800 border-teal-200",
        label: "Document",
      },
      payment: {
        color: "bg-emerald-100 text-emerald-800 border-emerald-200",
        label: "Payment",
      },
    };

    const config =
      configs[type as keyof typeof configs] || configs.consultation;
    return (
      <Badge variant="outline" className={`text-xs ${config.color}`}>
        {config.label}
      </Badge>
    );
  };

  const handleDownload = async () => {
    const rawSelectedActions = displayedActions.filter((a) =>
      a.id === "vitals-consolidated" ||
      a.id === "referral-consolidated" ||
      a.id === "consultation-documents-consolidated"
        ? a.data?.containerActionIds?.some((id) =>
            selectedActionIds.includes(id),
          )
        : selectedActionIds.includes(a.actionId),
    );

    const selectedActions = await Promise.all(
      rawSelectedActions.map(async (action) => {
        if (
          action.id === "referral-consolidated" &&
          patientReferalLetter?.referalLetter
        ) {
          const imageBase64s = [];
          const referralLetters = Array.isArray(
            patientReferalLetter.referalLetter,
          )
            ? patientReferalLetter.referalLetter
            : [patientReferalLetter.referalLetter];

          for (const pdf of referralLetters) {
            if (isBase64Pdf(pdf)) {
              const images = await pdfBase64ToImage(pdf);
              imageBase64s.push(...images);
            }
          }
          return { ...action, renderedDetails: imageBase64s };
        }

        if (
          action.id === "consultation-documents-consolidated" &&
          action.data?.documents
        ) {
          const imageBase64s = [];
          for (const doc of action.data.documents) {
            if (doc.fileContent) {
              // Check if it's a PDF
              if (isBase64Pdf(doc.fileContent)) {
                const images = await pdfBase64ToImage(doc.fileContent);
                imageBase64s.push(...images);
              }
              // Check if it's an image file
              else if (
                doc.fileType &&
                /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(doc.fileType)
              ) {
                const imageUrl = `data:image/${doc.fileType?.replace(".", "")};base64,${doc.fileContent}`;
                imageBase64s.push(imageUrl);
              }
              // For text files, convert to image
              else if (
                doc.fileType &&
                /\.(txt|csv|log|json|xml)$/i.test(doc.fileType)
              ) {
                try {
                  const textImage = await textBase64ToImage(doc.fileContent);
                  if (textImage) {
                    imageBase64s.push(textImage);
                  }
                } catch (e) {
                  // Skip if conversion fails
                }
              }
            }
          }
          return { ...action, renderedDetails: imageBase64s };
        }

        const pdfContent = action.data?.pdfContent || action.details;
        if (isBase64Pdf(pdfContent)) {
          const imageBase64s = await pdfBase64ToImage(pdfContent);
          return { ...action, renderedDetails: imageBase64s };
        }

        if (isBase64ImageData(action.details)) {
          return { ...action, renderedDetails: action.details };
        }

        return { ...action, renderedDetails: null };
      }),
    );

    // Create a printable version of the document
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Pharmacy Consultation Summary - ${patientName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; color: #333; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .patient-info { background: #f5f5f5; padding: 15px; margin: 10px 0; border-radius: 5px; }
            .action-item { margin: 10px 0; padding: 10px; border-left: 3px solid #0066cc; background: #f9f9f9; }
            .summary-section { margin: 20px 0; padding: 15px; background: #f0f8ff; border-radius: 5px; white-space: pre-wrap; }
            .signature-section { margin: 20px 0; padding: 15px; border: 1px solid #ddd; }
            .signature-image { max-width: 200px; height: auto; border: 1px solid #ccc; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #0066cc; padding: 8px; text-align: left; }
            th { background: #f0f8ff; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Pharmacy Consultation Summary</h1>
            <p><strong>Date:</strong> ${consultationDate} ${consultationTime}</p>
          </div>
          
          <div class="patient-info">
            <h2>Patient Information</h2>
            <p><strong>Name:</strong> ${patientName}</p>
            <p><strong>NHS Number:</strong> ${patientNHS}</p>
            <p><strong>Pharmacist:</strong> ${pharmacistName}</p>
          </div>
          
          ${
            isAiSummarySelected && localSummary
              ? `
            <div class="summary-section">
              <h2>Consultation Summary</h2>
              ${localSummary}
            </div>
          `
              : ""
          }
          
          <div>
            <h2>Actions Completed (${selectedActions.length})</h2>
            ${selectedActions
              .map((action) => {
                if (action.type === "vitals" && action.data?.vitalsData) {
                  const vitals = action.data.vitalsData;
                  return `
                  <div class="action-item" style="border: 2px solid #0066cc; border-radius: 8px; padding: 20px; background: white; margin-bottom: 20px;">
                    <h3 style="text-align: center; text-decoration: underline; margin-bottom: 20px;">Vitals Record</h3>
                    <p><strong>Patient Name:</strong> ${patientName}</p>
                    <p><strong>NHS Number:</strong> ${patientNHS}</p>
                    <p style="margin-bottom: 20px;">The following vitals were recorded during the consultation:</p>
                    <table style="width: 100%; border-collapse: collapse;">
                      <thead>
                        <tr style="background: #f0f8ff;">
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Vital</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Value</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${
                          vitals.heartRate
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Heart Rate</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.heartRate.Value ||
                              vitals.heartRate.value ||
                              vitals.heartRate
                            } ${
                              vitals.heartRate.Unit ||
                              vitals.heartRate.unit ||
                              "BPM"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(
                              vitals.heartRate.recordedAt,
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.weight
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Weight</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.weight.Value ||
                              vitals.weight.value ||
                              vitals.weight
                            } ${
                              vitals.weight.Unit || vitals.weight.unit || "kg"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(vitals.weight.recordedAt).toLocaleString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.height
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Height</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.height.Value ||
                              vitals.height.value ||
                              vitals.height
                            } ${
                              vitals.height.Unit || vitals.height.unit || "cm"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(vitals.height.recordedAt).toLocaleString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.temperature
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Temperature</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.temperature.Value ||
                              vitals.temperature.value ||
                              vitals.temperature
                            } ${
                              vitals.temperature.Unit ||
                              vitals.temperature.unit ||
                              "°C"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(
                              vitals.temperature.recordedAt,
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.respiratoryRate
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Respiratory Rate</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.respiratoryRate.Value ||
                              vitals.respiratoryRate.value ||
                              vitals.respiratoryRate
                            } ${
                              vitals.respiratoryRate.Unit ||
                              vitals.respiratoryRate.unit ||
                              "breaths/min"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(
                              vitals.respiratoryRate.recordedAt,
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.bloodPressure
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Blood Pressure</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                              ${
                                vitals.bloodPressure.Systolic?.Value ||
                                vitals.bloodPressure.systolic?.value ||
                                vitals.bloodPressure.systolic
                              }/${
                                vitals.bloodPressure.Diastolic?.Value ||
                                vitals.bloodPressure.diastolic?.value ||
                                vitals.bloodPressure.diastolic
                              } 
                              ${
                                vitals.bloodPressure.Systolic?.Unit ||
                                vitals.bloodPressure.systolic?.unit ||
                                vitals.bloodPressure.unit ||
                                "mmHg"
                              }
                            </td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(
                              vitals.bloodPressure.recordedAt,
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.circumference
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Circumference</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                              ${
                                vitals.circumference.Value ||
                                vitals.circumference.value ||
                                vitals.circumference
                              } ${
                                vitals.circumference.Unit ||
                                vitals.circumference.unit ||
                                "cm"
                              }
                            </td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.circumference.recordedAt
                                ? new Date(
                                    vitals.circumference.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.bmi
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">BMI</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                              ${
                                vitals.bmi.Value ||
                                vitals.bmi.value ||
                                vitals.bmi
                              } ${vitals.bmi.Unit || vitals.bmi.unit || "kg/m²"}
                            </td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${new Date(vitals.bmi.recordedAt).toLocaleString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.OxygenSaturation
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Oxygen Saturation</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.OxygenSaturation.Value ||
                              vitals.OxygenSaturation.value ||
                              vitals.OxygenSaturation
                            } ${
                              vitals.OxygenSaturation.Unit ||
                              vitals.OxygenSaturation.unit ||
                              "%"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.OxygenSaturation.recordedAt
                                ? new Date(
                                    vitals.OxygenSaturation.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.PainScore
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">NEWS Pain Score</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.PainScore.Value ||
                              vitals.PainScore.value ||
                              vitals.PainScore
                            } ${
                              vitals.PainScore.Unit ||
                              vitals.PainScore.unit ||
                              ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.PainScore.recordedAt
                                ? new Date(
                                    vitals.PainScore.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.GlucoseLevel
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Blood Sugar</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.GlucoseLevel.Value ||
                              vitals.GlucoseLevel.value ||
                              vitals.GlucoseLevel
                            } ${
                              vitals.GlucoseLevel.Unit ||
                              vitals.GlucoseLevel.unit ||
                              "mmol/L"
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.GlucoseLevel.recordedAt
                                ? new Date(
                                    vitals.GlucoseLevel.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.Consciousness
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Consciousness (AVPU)</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.Consciousness.Value ||
                              vitals.Consciousness.value ||
                              vitals.Consciousness
                            } ${
                              vitals.Consciousness.Unit ||
                              vitals.Consciousness.unit ||
                              ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.Consciousness.recordedAt
                                ? new Date(
                                    vitals.Consciousness.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }
                        ${
                          vitals.SupplementalOxygen
                            ? `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">Supplemental Oxygen</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              vitals.SupplementalOxygen.Value ||
                              vitals.SupplementalOxygen.value ||
                              vitals.SupplementalOxygen
                            } ${
                              vitals.SupplementalOxygen.Unit ||
                              vitals.SupplementalOxygen.unit ||
                              ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">
                            ${
                              vitals.SupplementalOxygen.recordedAt
                                ? new Date(
                                    vitals.SupplementalOxygen.recordedAt,
                                  ).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            </td>
                          </tr>
                        `
                            : ""
                        }

                      </tbody>
                    </table>
                    <div style="margin-top: 20px;">
                      <p><strong>Vitals recorded by:</strong></p>
                      <p>${pharmacistName}</p>
                    </div>
                  </div>
                `;
                }

                if (action.type === "vaccine" && action.data?.vaccineData) {
                  const vaccineData = action.data.vaccineData;
                  const vaccs = vaccineData.vaccinations || [];
                  return `
                  <div style="page-break-before: always; border: 2px solid #0066cc; border-radius: 8px; padding: 20px; background: white; margin-bottom: 20px;">
                    <div style="text-align: center; border-bottom: 2px solid #0066cc; margin-bottom: 20px; padding-bottom: 10px;">
                      <h2 style="color: #0066cc; margin: 0; font-size: 24px; text-transform: uppercase;">${
                        currentPharmacy?.teamPharmacyName || ""
                      }</h2>
                      <p style="margin: 5px 0; font-size: 14px;">${(currentPharmacy?.teamPharmacyAddress || "").replace(/\n/g, "<br>")}</p>
                      <p style="margin: 5px 0; font-size: 14px;">Email: ${
                        pharmacyContactDetails?.emailAddress || ""
                      }</p>
                      <p style="margin: 5px 0; font-size: 14px;">Tel: ${
                        pharmacyContactDetails?.phoneNumber || ""
                      }</p>
                    </div>
                    <h3 style="text-align: center; text-decoration: underline; margin-bottom: 20px;">Vaccination Certificate</h3>
                    <p><strong>Patient Name:</strong> ${patientName}</p>
                    <p><strong>NHS Number:</strong> ${patientNHS}</p>
                    <p style="margin-bottom: 20px;">This certificate certifies that the above patient has had the following vaccinations:</p>
                    <table style="width: 100%; border-collapse: collapse;">
                      <thead>
                        <tr style="background: #f0f8ff;">
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Vaccination</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Date Administered</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Vaccine Site</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Batch Number</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Expiry</th>
                          <th style="border: 1px solid #0066cc; padding: 8px; text-align: left;">Next Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${vaccs
                          .map(
                            (v) => `
                          <tr>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              v.vaccineName || ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${consultationDate}</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              v.site || ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              v.batchNumber || ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;">${
                              v.expiryDate || ""
                            }</td>
                            <td style="border: 1px solid #0066cc; padding: 8px;"></td>
                          </tr>
                        `,
                          )
                          .join("")}
                      </tbody>
                    </table>
                    <div style="margin-top: 20px;">
                      <p><strong>Vaccination administered by:</strong></p>
                      <p>${pharmacistName}</p>
                    </div>
                  </div>
                `;
                }

                if (
                  action.type === "prescription" &&
                  action.data?.prescriptionData
                ) {
                  const prescriptionData = action.data.prescriptionData;
                  return `
                  <div style="page-break-before: always; border: 2px solid #0066cc; border-radius: 8px; padding: 20px; background: white; margin-bottom: 20px;">
                    <h3 style="text-align: center; text-decoration: underline; margin-bottom: 20px;">Prescription</h3>
                    <div style="margin-bottom: 20px; padding: 15px; background: #f0f8ff; border-radius: 8px;">
                      <p><strong>Patient Name:</strong> ${patientName}</p>
                      <p><strong>NHS Number:</strong> ${patientNHS}</p>
                    </div>
                    <div style="margin-bottom: 20px;">
                      <h3 style="font-size: 16px; font-weight: bold;">Prescription Details</h3>
                      ${
                        prescriptionData?.base64Image
                          ? `<div style="margin: 15px 0; width: 100%;"><img src="${prescriptionData.base64Image}" alt="Prescription" style="width: 100%; height: auto; border: 1px solid #ddd; border-radius: 4px; object-fit: contain;" /></div>`
                          : prescriptionData?.items &&
                              prescriptionData.items.length > 0
                            ? prescriptionData.items
                                .map(
                                  (item: any, idx: number) => `
                              <div style="margin-bottom: 15px; padding: 10px; background: #f9f9f9; border: 1px solid #ddd; border-radius: 8px;">
                                <p><strong>${idx + 1}. ${
                                  item.medication
                                }</strong></p>
                                <p>Quantity: ${item.quantity}</p>
                                ${
                                  item.dosage
                                    ? `<p>Dosage: ${item.dosage}</p>`
                                    : ""
                                }
                              </div>
                            `,
                                )
                                .join("")
                            : "<p>No prescription details available.</p>"
                      }
                    </div>
                    <div style="margin-top: 20px;">
                      <p><strong>Prescribed by:</strong></p>
                      <p>${pharmacistName}</p>
                    </div>
                  </div>
                `;
                }

                return `
                <div class="action-item">
                  <h3>${
                    action.type.charAt(0).toUpperCase() + action.type.slice(1)
                  }</h3>
                  <p><strong>Description:</strong> ${action.description}</p>
                  ${
                    Array.isArray(action.renderedDetails)
                      ? action.renderedDetails
                          .map(
                            (src) =>
                              `<div style="margin: 15px 0;"><img src="${src}" style="max-width:100%;height:auto;margin-top:8px;" /></div>`,
                          )
                          .join("")
                      : isBase64TextData(
                            action.renderedDetails ||
                              action.details ||
                              (action.data as any)?.pdfContent,
                          )
                        ? `<div style="margin: 10px 0; padding: 15px; border: 1px solid #ddd; background: #fff; white-space: pre-wrap; font-family: monospace; font-size: 11px;">${decodeBase64Text(
                            action.renderedDetails ||
                              action.details ||
                              (action.data as any)?.pdfContent,
                          )}</div>`
                        : action.renderedDetails ||
                            (action.data as any)?.pdfContent
                          ? isBase64ImageData(
                              action.renderedDetails ||
                                action.details ||
                                (action.data as any)?.pdfContent,
                            )
                            ? `<div style="margin: 15px 0;"><img src="${getImageUrl(
                                action.renderedDetails ||
                                  action.details ||
                                  (action.data as any)?.pdfContent,
                              )}" alt="Action content" style="max-width: 100%; height: auto; border: 1px solid #ddd; border-radius: 4px;" /></div>`
                            : `<p><strong>Details:</strong> ${action.details}</p>`
                          : ""
                  }
                  <p><strong>Completed:</strong> ${new Date(
                    action.timestamp,
                  ).toLocaleString("en-GB")}</p>
                </div>
              `;
              })
              .join("")}
          </div>

          ${
            effectivePatientSignature &&
            typeof effectivePatientSignature === "object"
              ? `
            <div class="signature-section">
              <h3>Patient Consent & Signature</h3>
              <p><strong>Consent:</strong> ${
                effectivePatientSignature.consentText
              }</p>
              <p><strong>Signed by:</strong> ${
                effectivePatientSignature.patientName
              }</p>
              <p><strong>Date & Time:</strong> ${new Date(
                effectivePatientSignature.timestamp,
              ).toLocaleString("en-GB")}</p>
              <img src="${
                effectivePatientSignature.signature
              }" alt="Patient Signature" class="signature-image" />
            </div>
          `
              : ""
          }
          
          ${
            effectivePharmacistSignature &&
            typeof effectivePharmacistSignature === "object"
              ? `
            <div class="signature-section">
              <h3>Pharmacist Signature</h3>
              <p><strong>Pharmacist:</strong> ${
                effectivePharmacistSignature.patientName
              }</p>
              <p><strong>Date & Time:</strong> ${new Date(
                effectivePharmacistSignature.timestamp,
              ).toLocaleString("en-GB")}</p>
              <img src="${
                effectivePharmacistSignature.signature
              }" alt="Pharmacist Signature" class="signature-image" />
            </div>
          `
              : "<div class='signature-section'><p>No pharmacist signature captured</p></div>"
          }
          
          <div class="footer">
            <p>This document was generated electronically on ${new Date().toLocaleString(
              "en-GB",
            )}</p>
            <p>© 2025 Pharmacy Management System</p>
          </div>
        </body>
        </html>
      `;
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handlePrint = () => {
    handleDownload();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const selectedActions = displayedActions.filter((a) =>
    a.id === "vitals-consolidated" ||
    a.id === "referral-consolidated" ||
    a.id === "consultation-documents-consolidated"
      ? a.data?.containerActionIds?.some((id) => selectedActionIds.includes(id))
      : selectedActionIds.includes(a.actionId),
  );
  const formatUKDate = (rawDate) => {
    if (!rawDate) return "";

    const normalized = rawDate.replace(",", "");

    const [datePart, timePart] = normalized.split(" ");
    const [day, month, year] = datePart.split("/");

    const parsedDate = new Date(`${year}-${month}-${day}T${timePart}`);

    if (isNaN(parsedDate.getTime())) return "";

    return parsedDate.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      onClick={handleBackdropClick}
    >
      <div
        className={`w-full max-w-4xl h-[90vh] rounded-lg shadow-xl ${
          isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"
        } border overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2
              className={`text-xl font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Consultation Summary Document
            </h2>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 p-6 space-y-6 overflow-y-auto">
          {/* Header Information */}
          <div
            className={`p-4 rounded-lg ${
              isDarkMode ? "bg-gray-700" : "bg-blue-50"
            }`}
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3
                  className={`font-semibold mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Patient Information
                </h3>
                <div className="space-y-1">
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <span className="font-medium">Name:</span> {patientName}
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <span className="font-medium">NHS:</span> {patientNHS}
                  </div>
                </div>
              </div>
              <div>
                <h3
                  className={`font-semibold mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Consultation Details
                </h3>
                <div className="space-y-1">
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <span className="font-medium">Pharmacist:</span>{" "}
                    {pharmacistName}
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    <span className="font-medium">Date:</span>{" "}
                    {consultationDate} {consultationTime}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Select Actions Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Select Actions to Include (
                {displayedActions.filter((action) =>
                  action.id === "vitals-consolidated"
                    ? action.data?.containerActionIds?.every((id) =>
                        selectedActionIds.includes(id),
                      )
                    : selectedActionIds.includes(action.actionId),
                ).length + (isAiSummarySelected ? 1 : 0)}
                /{displayedActions.length + 1})
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAll}
                  className={`text-xs ${
                    isDarkMode
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAll}
                  className={`text-xs ${
                    isDarkMode
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Deselect All
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {/* Summary Action */}
              <div
                className={`p-3 rounded-lg border transition-colors ${
                  isAiSummarySelected
                    ? isDarkMode
                      ? "bg-gray-700 border-purple-500"
                      : "bg-purple-50 border-purple-300"
                    : isDarkMode
                      ? "bg-gray-700 border-gray-600 hover:border-gray-500"
                      : "bg-gray-50 border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={isAiSummarySelected}
                    onCheckedChange={(checked) => {
                      setIsAiSummarySelected(checked === true);
                    }}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      <span
                        className={`font-medium text-sm ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Summary
                      </span>
                      <Badge
                        variant="outline"
                        className="text-xs text-purple-700 bg-purple-100 border-purple-300"
                      >
                        AI
                      </Badge>
                    </div>
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      } ml-6 mb-2`}
                    >
                      Summary of the consultation based on selected actions
                    </p>

                    {isAiSummarySelected && (
                      <div className="mt-3 ml-6 space-y-3">
                        <Button
                          size="sm"
                          onClick={generateAiSummary}
                          disabled={isGeneratingSummary}
                          className="text-white bg-black hover:bg-gray-900"
                        >
                          {isGeneratingSummary ? (
                            <>
                              <Sparkles className="w-4 h-4 mr-2 " />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              {localSummary ? "Regenerate" : "Generate"} Summary
                            </>
                          )}
                        </Button>

                        {localSummary && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                              <Edit3 className="w-3 h-3" />
                              <span>Edit the summary below as needed</span>
                            </div>
                            <Textarea
                              value={localSummary}
                              onChange={(e) => {
                                setLocalSummary(e.target.value);
                                setPatientSummaryForActions(e.target.value);
                              }}
                              rows={8}
                              className={`text-xs font-mono ${
                                isDarkMode
                                  ? "bg-gray-800 border-gray-600 text-gray-200"
                                  : "bg-white border-gray-300"
                              }`}
                            />
                          </div>
                        )}

                        {!localSummary && (
                          <p
                            className={`text-xs ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Click "Generate Summary" to create a summary
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Regular Actions */}
              {isSummaryActionLoading ? (
                <SummaryActionsSkeleton isDarkMode={isDarkMode} count={3} />
              ) : displayedActions.length > 0 ? (
                <>
                  {displayedActions.map((action) => (
                    <div
                      key={`action-${action.id}`}
                      className={`p-3 rounded-lg border transition-colors ${
                        action.id === "vitals-consolidated" ||
                        action.id === "referral-consolidated" ||
                        action.id === "consultation-documents-consolidated"
                          ? action.data?.containerActionIds?.some((id) =>
                              selectedActionIds.includes(id),
                            )
                            ? isDarkMode
                              ? "bg-gray-700 border-blue-500"
                              : "bg-blue-50 border-blue-300"
                            : isDarkMode
                              ? "bg-gray-700 border-gray-600 hover:border-gray-500"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                          : selectedActionIds.includes(action.actionId)
                            ? isDarkMode
                              ? "bg-gray-700 border-blue-500"
                              : "bg-blue-50 border-blue-300"
                            : isDarkMode
                              ? "bg-gray-700 border-gray-600 hover:border-gray-500"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={
                            action.id === "vitals-consolidated" ||
                            action.id === "referral-consolidated" ||
                            action.id === "consultation-documents-consolidated"
                              ? action.data?.containerActionIds?.every((id) =>
                                  selectedActionIds.includes(id),
                                )
                              : selectedActionIds.includes(action.actionId)
                          }
                          onCheckedChange={() => {
                            if (
                              (action.id === "vitals-consolidated" ||
                                action.id === "referral-consolidated" ||
                                action.id ===
                                  "consultation-documents-consolidated") &&
                              action.data?.containerActionIds
                            ) {
                              const allSelected =
                                action.data.containerActionIds.every((id) =>
                                  selectedActionIds.includes(id),
                                );
                              if (allSelected) {
                                setSelectedActionIds((prev) =>
                                  prev.filter(
                                    (id) =>
                                      !action.data.containerActionIds.includes(
                                        id,
                                      ),
                                  ),
                                );
                              } else {
                                setSelectedActionIds((prev) =>
                                  Array.from(
                                    new Set([
                                      ...prev,
                                      ...action.data.containerActionIds,
                                    ]),
                                  ),
                                );
                              }
                            } else {
                              toggleAction(action.actionId);
                            }
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getActionIcon(action.type)}
                            <span
                              className={`font-medium text-sm ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {action.description ===
                              "CreateTreatmentPlan completed"
                                ? "Treatment Plan"
                                : action.description}
                            </span>
                            {action.type !== "referral" &&
                              getActionBadge(action.type)}
                          </div>
                          {action.id ===
                            "consultation-documents-consolidated" &&
                          action.data?.documents ? (
                            <div className="mt-2 ml-6 space-y-4">
                              {action.data.documents.map(
                                (doc: any, index: number) => (
                                  <div
                                    key={`cons-doc-${index}`}
                                    className="w-full"
                                  >
                                    <p
                                      className={`text-xs font-semibold mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                    >
                                      {doc.title}
                                    </p>
                                    {doc.fileContent && (
                                      <div className="w-full overflow-hidden bg-white border rounded">
                                        <PdfImage
                                          base64={doc.fileContent}
                                          fileType={doc.fileType}
                                          className="w-full h-auto"
                                          onConverted={(imageUrl) => {
                                            if (
                                              doc.fileContent &&
                                              !pdfImageCache[doc.fileContent]
                                            ) {
                                              setPdfImageCache((prev) => ({
                                                ...prev,
                                                [doc.fileContent]: imageUrl,
                                              }));
                                            }
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ),
                              )}
                            </div>
                          ) : action.type === "vitals" &&
                            action.data?.vitalsData ? (
                            <div className="mt-4 ml-1">
                              <div
                                className={`p-4 rounded-lg border-2 bg-white ${
                                  isDarkMode
                                    ? "border-blue-500 bg-gray-800"
                                    : "border-blue-500"
                                }`}
                              >
                                <h2 className="pb-1 mb-3 text-base font-bold text-blue-600 border-b border-blue-500">
                                  Vitals Record
                                </h2>
                                <div className="overflow-x-auto text-gray-900">
                                  <table className="w-full text-xs border border-collapse border-blue-500">
                                    <thead>
                                      <tr className="bg-blue-50">
                                        <th className="p-1 text-left border border-blue-500">
                                          Vital
                                        </th>
                                        <th className="p-1 text-left border border-blue-500">
                                          Value
                                        </th>
                                        <th className="p-1 text-left border border-blue-500">
                                          Date
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Array.isArray(
                                        action.data.vitalsData.news2Vitals,
                                      ) &&
                                        action.data.vitalsData.news2Vitals.map(
                                          (vital, index) => (
                                            <tr
                                              key={`news2-vital-${vital.vitalType || index}`}
                                            >
                                              <td className="p-1 border border-blue-500">
                                                {vital.label ||
                                                  vital.vitalType ||
                                                  "NEWS2 Vital"}
                                              </td>
                                              <td className="p-1 border border-blue-500">
                                                {vital.Value ||
                                                  vital.value ||
                                                  ""}{" "}
                                                {vital.Unit || vital.unit || ""}
                                              </td>
                                              <td className="p-1 border border-blue-500">
                                                {(() => {
                                                  const dateStr =
                                                    vital.recordedAt ||
                                                    action.data.vitalsData
                                                      .recordedAt ||
                                                    action.data.vitalsData
                                                      .timestamp ||
                                                    action.timestamp;
                                                  if (!dateStr)
                                                    return "No date";
                                                  const date = new Date(
                                                    dateStr,
                                                  );
                                                  if (isNaN(date.getTime()))
                                                    return "no date";
                                                  return date.toLocaleString(
                                                    "en-GB",
                                                    {
                                                      day: "2-digit",
                                                      month: "short",
                                                      year: "numeric",
                                                      hour: "2-digit",
                                                      minute: "2-digit",
                                                    },
                                                  );
                                                })()}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      {action.data.vitalsData.heartRate && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Heart Rate
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData.heartRate
                                              .Value ||
                                              action.data.vitalsData.heartRate
                                                .value ||
                                              action.data.vitalsData
                                                .heartRate}{" "}
                                            {action.data.vitalsData.heartRate
                                              .Unit ||
                                              action.data.vitalsData.heartRate
                                                .unit ||
                                              "BPM"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {new Date(
                                              action.data.vitalsData.heartRate
                                                .recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData.weight && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Weight
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData.weight
                                              .Value ||
                                              action.data.vitalsData.weight
                                                .value ||
                                              action.data.vitalsData
                                                .weight}{" "}
                                            {action.data.vitalsData.weight
                                              .Unit ||
                                              action.data.vitalsData.weight
                                                .unit ||
                                              "kg"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {/* {formatUKDate(
                                              action.data.vitalsData.weight
                                                .recordedAt,
                                            )} */}
                                            {new Date(
                                              action.data.vitalsData.weight
                                                .recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData.height && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Height
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData.height
                                              .Value ||
                                              action.data.vitalsData.height
                                                .value ||
                                              action.data.vitalsData
                                                .height}{" "}
                                            {action.data.vitalsData.height
                                              .Unit ||
                                              action.data.vitalsData.height
                                                .unit ||
                                              "cm"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {/* {formatUKDate(
                                              action.data.vitalsData.height
                                                .recordedAt,
                                            )} */}
                                            {new Date(
                                              action.data.vitalsData.height
                                                .recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData.temperature && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Temperature
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData.temperature
                                              .Value ||
                                              action.data.vitalsData.temperature
                                                .value ||
                                              action.data.vitalsData
                                                .temperature}{" "}
                                            {action.data.vitalsData.temperature
                                              .Unit ||
                                              action.data.vitalsData.temperature
                                                .unit ||
                                              "°C"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {/* {formatUKDate(
                                              action.data.vitalsData.temperature
                                                .recordedAt,
                                            )} */}
                                            {new Date(
                                              action.data.vitalsData.temperature
                                                .recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData
                                        .respiratoryRate && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Respiratory Rate
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData
                                              .respiratoryRate.Value ||
                                              action.data.vitalsData
                                                .respiratoryRate.value ||
                                              action.data.vitalsData
                                                .respiratoryRate}{" "}
                                            {action.data.vitalsData
                                              .respiratoryRate.Unit ||
                                              action.data.vitalsData
                                                .respiratoryRate.unit ||
                                              "breaths/min"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {/* {formatUKDate(
                                              action.data.vitalsData
                                                .respiratoryRate.recordedAt,
                                            )} */}
                                            {new Date(
                                              action.data.vitalsData
                                                .respiratoryRate.recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData
                                        .circumferenceValue && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Circumference
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData
                                              .circumference.Value ||
                                              action.data.vitalsData
                                                .circumference.value}{" "}
                                            {action.data.vitalsData
                                              .circumference.Unit ||
                                              action.data.vitalsData
                                                .circumference.unit ||
                                              "cm"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {(() => {
                                              const recordedAt =
                                                action.data.vitalsData
                                                  .circumference.recordedAt;
                                              if (!recordedAt) return "—";
                                              const date = new Date(recordedAt);
                                              if (isNaN(date.getTime()))
                                                return "—";
                                              return date.toLocaleString(
                                                "en-GB",
                                                {
                                                  day: "2-digit",
                                                  month: "short",
                                                  year: "numeric",
                                                  hour: "2-digit",
                                                  minute: "2-digit",
                                                  hour12: false,
                                                },
                                              );
                                            })()}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData.bmi && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            BMI
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData.bmi.Value}{" "}
                                            {action.data.vitalsData.bmi.Unit ||
                                              "kg/m²"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {/* {formatUKDate(
                                              action.data.vitalsData.bmi
                                                .recordedAt,
                                            )} */}
                                            {new Date(
                                              action.data.vitalsData.bmi
                                                .recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData.bloodPressure && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Blood Pressure
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData
                                              .bloodPressure.Systolic?.Value ||
                                              action.data.vitalsData
                                                .bloodPressure.systolic
                                                ?.value ||
                                              action.data.vitalsData
                                                .bloodPressure.systolic}
                                            /
                                            {action.data.vitalsData
                                              .bloodPressure.Diastolic?.Value ||
                                              action.data.vitalsData
                                                .bloodPressure.diastolic
                                                ?.value ||
                                              action.data.vitalsData
                                                .bloodPressure.diastolic}{" "}
                                            {action.data.vitalsData
                                              .bloodPressure.Systolic?.Unit ||
                                              action.data.vitalsData
                                                .bloodPressure.systolic?.unit ||
                                              action.data.vitalsData
                                                .bloodPressure.unit ||
                                              "mmHg"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {/* {formatUKDate(
                                              action.data.vitalsData
                                                .bloodPressure.recordedAt,
                                            )} */}
                                            {new Date(
                                              action.data.vitalsData
                                                .bloodPressure.recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData
                                        .OxygenSaturation && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Oxygen Saturation
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData
                                              .OxygenSaturation.Value ||
                                              action.data.vitalsData
                                                .OxygenSaturation.value ||
                                              action.data.vitalsData
                                                .OxygenSaturation}{" "}
                                            {action.data.vitalsData
                                              .OxygenSaturation.Unit ||
                                              action.data.vitalsData
                                                .OxygenSaturation.unit ||
                                              "%"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {new Date(
                                              action.data.vitalsData
                                                .OxygenSaturation.recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData.PainScore && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            NEWS Pain Score
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData.PainScore
                                              .Value ||
                                              action.data.vitalsData.PainScore
                                                .value ||
                                              action.data.vitalsData
                                                .PainScore}{" "}
                                            {action.data.vitalsData.PainScore
                                              .Unit ||
                                              action.data.vitalsData.PainScore
                                                .unit ||
                                              "%"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {new Date(
                                              action.data.vitalsData.PainScore
                                                .recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData.GlucoseLevel && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Blood Sugar
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData.GlucoseLevel
                                              .Value ||
                                              action.data.vitalsData
                                                .GlucoseLevel.value ||
                                              action.data.vitalsData
                                                .GlucoseLevel}{" "}
                                            {action.data.vitalsData.GlucoseLevel
                                              .Unit ||
                                              action.data.vitalsData
                                                .GlucoseLevel.unit ||
                                              "%"}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {new Date(
                                              action.data.vitalsData
                                                .GlucoseLevel.recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData.Consciousness && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Consciousness (AVPU)
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData
                                              .Consciousness.Value ||
                                              action.data.vitalsData
                                                .Consciousness.value ||
                                              action.data.vitalsData
                                                .Consciousness}{" "}
                                            {action.data.vitalsData
                                              .Consciousness.Unit ||
                                              action.data.vitalsData
                                                .Consciousness.unit ||
                                              ""}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {new Date(
                                              action.data.vitalsData
                                                .Consciousness.recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                      {action.data.vitalsData
                                        .SupplementalOxygen && (
                                        <tr>
                                          <td className="p-1 border border-blue-500">
                                            Supplemental Oxygen
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {action.data.vitalsData
                                              .SupplementalOxygen.Value ||
                                              action.data.vitalsData
                                                .SupplementalOxygen.value ||
                                              action.data.vitalsData
                                                .SupplementalOxygen}{" "}
                                            {action.data.vitalsData
                                              .SupplementalOxygen.Unit ||
                                              action.data.vitalsData
                                                .SupplementalOxygen.unit ||
                                              ""}
                                          </td>
                                          <td className="p-1 border border-blue-500">
                                            {new Date(
                                              action.data.vitalsData
                                                .SupplementalOxygen.recordedAt,
                                            ).toLocaleString("en-GB", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          ) : action.type === "referral" &&
                            patientReferalLetter?.referalLetter &&
                            Array.isArray(
                              patientReferalLetter?.referalLetter,
                            ) ? (
                            <div className="mt-2 ml-6 space-y-4">
                              {patientReferalLetter.referalLetter.map(
                                (pdfBase64, index) => (
                                  <div
                                    key={`referral-pdf-${index}`}
                                    className="w-full overflow-hidden bg-white border rounded"
                                  >
                                    {/* //here */}
                                    <PdfImage
                                      base64={pdfBase64}
                                      fileType=".pdf"
                                      className="w-full h-auto"
                                      onConverted={(imageUrl) => {
                                        if (
                                          pdfBase64 &&
                                          !pdfImageCache[pdfBase64]
                                        ) {
                                          setPdfImageCache((prev) => ({
                                            ...prev,
                                            [pdfBase64]: imageUrl,
                                          }));
                                        }
                                      }}
                                    />
                                  </div>
                                ),
                              )}
                            </div>
                          ) : (
                            (action.details || action.data?.pdfContent) && (
                              <div className="mt-2 ml-6">
                                {isBase64ImageData(action.details) ? (
                                  <div className="w-full">
                                    <img
                                      src={getImageUrl(action.details)}
                                      alt="Action detail"
                                      className="w-full h-auto border rounded"
                                    />
                                  </div>
                                ) : isBase64ImageData(
                                    action.data?.pdfContent,
                                  ) ? (
                                  <div className="w-full">
                                    <img
                                      src={getImageUrl(action.data.pdfContent)}
                                      alt="Document content"
                                      className="w-full h-auto border rounded"
                                    />
                                  </div>
                                ) : isBase64PdfData(action.data?.pdfContent) ||
                                  isBase64PdfData(action.details) ? (
                                  <div className="w-full overflow-hidden bg-white border rounded">
                                    <PdfImage
                                      base64={
                                        action.data?.pdfContent &&
                                        isBase64PdfData(action.data.pdfContent)
                                          ? action.data.pdfContent
                                          : action.details
                                      }
                                      fileType=".pdf"
                                      className="w-full h-auto"
                                      onConverted={(imageUrl) => {
                                        const content =
                                          action.data?.pdfContent ||
                                          action.details;
                                        if (
                                          content &&
                                          !pdfImageCache[content]
                                        ) {
                                          setPdfImageCache((prev) => ({
                                            ...prev,
                                            [content]: imageUrl,
                                          }));
                                        }
                                      }}
                                    />
                                  </div>
                                ) : isBase64TextData(action.details) ? (
                                  <div className="w-full p-3 bg-white border rounded shadow-sm text-gray-800 whitespace-pre-wrap font-mono text-[10px] max-h-40 overflow-y-auto">
                                    {decodeBase64Text(action.details)}
                                  </div>
                                ) : (
                                  <p
                                    className={`text-xs ${
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    {action.details}
                                  </p>
                                )}
                              </div>
                            )
                          )}
                          <div
                            className={`flex items-center gap-1 mt-1 text-xs ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            } ml-6`}
                          >
                            <Clock className="w-3 h-3" />
                            {new Date(action.timestamp).toLocaleString("en-GB")}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div
                  className={`text-center py-8 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No actions completed yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Signatures Section */}
          <div className="space-y-4">
            <h3
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Signatures
            </h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Patient Signature */}
              <div
                className={`p-4 rounded-lg border ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h4
                  className={`font-medium mb-3 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Patient Consent & Signature
                </h4>
                {effectivePatientSignature ? (
                  <div className="space-y-3">
                    <div
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <span className="font-medium">Consent:</span>
                      <p className="mt-1 italic">
                        "{effectivePatientSignature.consentText}"
                      </p>
                    </div>
                    <div className="flex items-center justify-center p-3 bg-white border rounded">
                      <img
                        src={effectivePatientSignature.signature}
                        alt="Patient Signature"
                        className="object-contain h-16 max-w-full"
                      />
                    </div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Signed by {effectivePatientSignature.patientName} on{" "}
                      {new Date(
                        effectivePatientSignature.timestamp,
                      ).toLocaleString("en-GB")}
                    </div>
                  </div>
                ) : (
                  <div
                    className={`text-center py-4 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <p className="text-sm">No patient signature captured</p>
                  </div>
                )}
              </div>

              {/* Pharmacist Signature */}
              <div
                className={`p-4 rounded-lg border ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h4
                  className={`font-medium mb-3 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Pharmacist Signature
                </h4>
                {effectivePharmacistSignature ? (
                  <div className="space-y-3">
                    <div
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <span className="font-medium">Pharmacist:</span>
                      <p className="mt-1 italic">
                        "{effectivePharmacistSignature.patientName}"
                      </p>
                    </div>

                    <div className="flex items-center justify-center p-3 bg-white border rounded">
                      <img
                        src={effectivePharmacistSignature.signature}
                        alt="Pharmacist Signature"
                        className="object-contain h-16 max-w-full"
                      />
                    </div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Signed by {effectivePharmacistSignature.patientName} on{" "}
                      {new Date(
                        effectivePharmacistSignature.timestamp,
                      ).toLocaleString("en-GB")}
                    </div>
                  </div>
                ) : (
                  <div
                    className={`text-center py-4 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <p className="text-sm">No pharmacist signature captured</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={handleGenerateDocument}
            disabled={selectedActionIds.length === 0 && !isAiSummarySelected}
            className="text-white bg-black hover:bg-gray-900"
          >
            <FileText className="w-4 h-4 mr-2" />
            Generate Preview
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            className={`${
              isDarkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
      </div>

      {/* Email Dialog */}
      {showEmailDialog && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          onClick={() => setShowEmailDialog(false)}
        >
          <div
            className={`w-full max-w-lg rounded-lg shadow-xl ${
              isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"
            } border p-6`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold flex items-center gap-2 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                <Mail className="w-5 h-5" />
                Email Consultation Summary
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEmailDialog(false)}
                className="w-8 h-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Recipient */}
              <div>
                <label
                  className={`text-sm font-medium mb-1 block ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Recipient Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder="patient@example.com"
                />
              </div>

              {/* Subject */}
              <div>
                <label
                  className={`text-sm font-medium mb-1 block ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Subject <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Consultation Summary"
                />
              </div>

              {/* Message */}
              <div>
                <label
                  className={`text-sm font-medium mb-1 block ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Message
                </label>
                <Textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={6}
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                  placeholder="Optional message to include in the email..."
                />
              </div>

              {/* Info */}
              <div
                className={`text-xs p-3 rounded ${
                  isDarkMode
                    ? "bg-blue-900/20 text-blue-300"
                    : "bg-blue-50 text-blue-700"
                }`}
              >
                The consultation summary document will be attached as a PDF to
                this email.
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEmailDialog(false)}
                className={`${
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={isSendingEmail || !emailRecipient || !emailSubject}
                className="text-white bg-black hover:bg-gray-900"
              >
                {isSendingEmail ? (
                  <>
                    <Send className="w-4 h-4 mr-2 " />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      {showPreview && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          onClick={() => setShowPreview(false)}
        >
          <div
            className={`w-full max-w-4xl h-[90vh] rounded-lg shadow-xl ${
              isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"
            } border overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview Header */}
            <div
              className={`flex-shrink-0 p-6 border-b ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <h3
                  className={`text-xl font-semibold flex items-center gap-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  Document Preview
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowPreview(false);
                    if (isDocumentGenerated) {
                      onClose();
                    }
                  }}
                  className="w-8 h-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Preview Content */}
            <div
              className={`flex-1 overflow-y-auto p-8 min-h-0 ${
                isDarkMode ? "bg-gray-900" : "bg-gray-50"
              }`}
            >
              <div
                className={`${
                  isDarkMode ? "bg-gray-800" : "bg-white"
                } rounded-lg p-8 space-y-6`}
              >
                {/* Document Header */}
                <div
                  className={`w-full border-b-2 pb-4 ${
                    isDarkMode ? "border-gray-600" : "border-gray-300"
                  }`}
                >
                  <h1
                    className={`text-2xl font-bold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Pharmacy Consultation Summary
                  </h1>
                  <p
                    className={`text-sm mt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <strong>Date:</strong> {consultationDate} {consultationTime}
                  </p>
                </div>

                {/* Patient Information */}
                <div
                  className={`w-full p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-700" : "bg-blue-50"
                  }`}
                >
                  <h2
                    className={`text-lg font-semibold mb-3 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Patient Information
                  </h2>
                  <div className="space-y-2">
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <strong>Name:</strong> {patientName}
                    </p>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <strong>NHS Number:</strong> {patientNHS}
                    </p>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      <strong>Pharmacist:</strong> {pharmacistName}
                    </p>
                  </div>
                </div>

                {/* AI Summary if selected */}
                {isAiSummarySelected && localSummary && (
                  <div
                    className={`w-full p-4 rounded-lg ${
                      isDarkMode
                        ? "bg-purple-900/20 border border-purple-500/30"
                        : "bg-purple-50 border border-purple-200"
                    }`}
                  >
                    <h2
                      className={`text-lg font-semibold mb-3 flex items-center gap-2 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      Consultation Summary
                    </h2>
                    <div
                      className={`text-sm whitespace-pre-wrap ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {localSummary}
                    </div>
                  </div>
                )}

                {/* Actions Completed */}
                {selectedActions.length > 0 && (
                  <div className="w-full">
                    <h2
                      className={`text-lg font-semibold mb-3 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Actions Completed ({selectedActions.length})
                    </h2>
                    <div className="w-full space-y-3">
                      {selectedActions.map((action) => {
                        if (
                          action.type === "vaccine" &&
                          action.data?.vaccineData
                        ) {
                          const vaccs =
                            action.data.vaccineData.vaccinations || [];
                          return (
                            <div
                              key={action.id}
                              className={`w-full p-6 rounded-lg border-2 mb-4 bg-white ${
                                isDarkMode
                                  ? "border-blue-500 bg-gray-800"
                                  : "border-blue-500"
                              }`}
                            >
                              <div className="pb-4 mb-6 text-center border-b-2 border-blue-500">
                                <h2 className="text-2xl font-bold text-blue-600 uppercase">
                                  {currentPharmacy?.teamPharmacyName || ""}
                                </h2>
                                <p className="text-xs text-gray-500">
                                  {(currentPharmacy?.teamPharmacyAddress || "")
                                    .split("\n")
                                    .map((line, index, arr) => (
                                      <React.Fragment key={index}>
                                        {line}
                                        {index < arr.length - 1 && <br />}
                                      </React.Fragment>
                                    ))}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Email:{" "}
                                  {pharmacyContactDetails?.emailAddress || ""}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Tel:{" "}
                                  {pharmacyContactDetails?.phoneNumber || ""}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {consultationDate}
                                </p>
                              </div>

                              <h3 className="mb-4 font-bold text-center text-gray-900 underline">
                                Vaccination Certificate
                              </h3>

                              <div className="mb-6 space-y-1 text-gray-900">
                                <p className="text-sm">
                                  <strong>Patient Name:</strong> {patientName}
                                </p>
                                <p className="text-sm">
                                  <strong>NHS Number:</strong> {patientNHS}
                                </p>
                                <p className="mt-4 text-sm">
                                  This certificate certifies that the above
                                  patient has had the following vaccinations:
                                </p>
                              </div>

                              <div className="overflow-x-auto">
                                <table className="w-full text-xs text-gray-900 border border-collapse border-blue-500">
                                  <thead>
                                    <tr className="bg-blue-50">
                                      <th className="p-2 text-left border border-blue-500">
                                        Vaccination
                                      </th>
                                      <th className="p-2 text-left border border-blue-500">
                                        Date Administered
                                      </th>
                                      <th className="p-2 text-left border border-blue-500">
                                        Vaccine Site
                                      </th>
                                      <th className="p-2 text-left border border-blue-500">
                                        Batch Number
                                      </th>
                                      <th className="p-2 text-left border border-blue-500">
                                        Expiry
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {vaccs.map((v: any, idx: number) => (
                                      <tr key={idx}>
                                        <td className="p-2 border border-blue-500">
                                          {v.vaccineName || ""}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {consultationDate}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {v.site || ""}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {v.batchNumber || ""}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {v.expiryDate || ""}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              <div className="mt-6 text-sm text-gray-900">
                                <p>
                                  <strong>Vaccination administered by:</strong>
                                </p>
                                <p>{pharmacistName}</p>
                              </div>
                            </div>
                          );
                        }

                        if (
                          (action.type === "vitals" ||
                            action.id === "vitals-consolidated") &&
                          action.data?.vitalsData
                        ) {
                          const vitals = action.data.vitalsData;
                          return (
                            <div
                              key={action.id}
                              className={`w-full p-6 rounded-lg border-2 mb-4 bg-white ${
                                isDarkMode
                                  ? "border-blue-500 bg-gray-800"
                                  : "border-blue-500"
                              }`}
                            >
                              <h3 className="mb-4 font-bold text-center text-gray-900 underline">
                                Vitals Record
                              </h3>
                              <div className="mb-6 space-y-1 text-gray-900">
                                <p className="text-sm">
                                  <strong>Patient Name:</strong> {patientName}
                                </p>
                                <p className="text-sm">
                                  <strong>NHS Number:</strong> {patientNHS}
                                </p>
                                <p className="mt-4 text-sm">
                                  The following vitals were recorded during the
                                  consultation:
                                </p>
                              </div>

                              <div className="overflow-x-auto">
                                <table className="w-full text-xs text-gray-900 border border-collapse border-blue-500">
                                  <thead>
                                    <tr className="bg-blue-50">
                                      <th className="p-2 text-left border border-blue-500">
                                        Vital
                                      </th>
                                      <th className="p-2 text-left border border-blue-500">
                                        Value
                                      </th>
                                      <th className="p-2 text-left border border-blue-500">
                                        Date
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Array.isArray(vitals.news2Vitals) &&
                                      vitals.news2Vitals.map(
                                        (vital, index) => (
                                          <tr
                                            key={`news2-vital-${vital.vitalType || index}`}
                                          >
                                            <td className="p-2 border border-blue-500">
                                              {vital.label ||
                                                vital.vitalType ||
                                                "NEWS2 Vital"}
                                            </td>
                                            <td className="p-2 border border-blue-500">
                                              {vital.Value ||
                                                vital.value ||
                                                ""}{" "}
                                              {vital.Unit || vital.unit || ""}
                                            </td>
                                            <td className="p-2 border border-blue-500">
                                              {(() => {
                                                const dateStr =
                                                  vital.recordedAt ||
                                                  vitals.recordedAt ||
                                                  vitals.timestamp ||
                                                  action.timestamp;
                                                if (!dateStr) return "No date";
                                                const date = new Date(dateStr);

                                                if (isNaN(date.getTime()))
                                                  return "no date";

                                                return date.toLocaleString(
                                                  "en-GB",
                                                  {
                                                    day: "2-digit",
                                                    month: "short",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                  },
                                                );
                                              })()}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    {vitals.heartRate && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Heart Rate
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.heartRate.Value ||
                                            vitals.heartRate.value ||
                                            vitals.heartRate}{" "}
                                          {vitals.heartRate.Unit ||
                                            vitals.heartRate.unit ||
                                            "BPM"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.heartRate.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);

                                            if (isNaN(date.getTime()))
                                              return "no date";

                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.weight && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Weight
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.weight.Value ||
                                            vitals.weight.value ||
                                            vitals.weight}{" "}
                                          {vitals.weight.Unit ||
                                            vitals.weight.unit ||
                                            "kg"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.weight.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);

                                            if (isNaN(date.getTime()))
                                              return "no date";

                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.height && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Height
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.height.Value ||
                                            vitals.height.value ||
                                            vitals.height}{" "}
                                          {vitals.height.Unit ||
                                            vitals.height.unit ||
                                            "cm"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.height.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);

                                            if (isNaN(date.getTime()))
                                              return "no date";

                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.temperature && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Temperature
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.temperature.Value ||
                                            vitals.temperature.value ||
                                            vitals.temperature}{" "}
                                          {vitals.temperature.Unit ||
                                            vitals.temperature.unit ||
                                            "°C"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.temperature.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);

                                            if (isNaN(date.getTime()))
                                              return "no date";

                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.respiratoryRate && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Respiratory Rate
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.respiratoryRate.Value ||
                                            vitals.respiratoryRate.value ||
                                            vitals.respiratoryRate}{" "}
                                          {vitals.respiratoryRate.Unit ||
                                            vitals.respiratoryRate.unit ||
                                            "breaths/min"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.respiratoryRate.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);

                                            if (isNaN(date.getTime()))
                                              return "no date";

                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.bloodPressure && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Blood Pressure
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.bloodPressure.Systolic
                                            ?.Value ||
                                            vitals.bloodPressure.systolic
                                              ?.value ||
                                            vitals.bloodPressure.systolic}
                                          /
                                          {vitals.bloodPressure.Diastolic
                                            ?.Value ||
                                            vitals.bloodPressure.diastolic
                                              ?.value ||
                                            vitals.bloodPressure.diastolic}{" "}
                                          {vitals.bloodPressure.Systolic
                                            ?.Unit ||
                                            vitals.bloodPressure.systolic
                                              ?.unit ||
                                            vitals.bloodPressure.unit ||
                                            "mmHg"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals?.bloodPressure?.recordedAt;

                                            if (!dateStr) return "No date";

                                            const date = new Date(dateStr);

                                            if (isNaN(date.getTime()))
                                              return "no date";

                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.circumference && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Circumference
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.circumference.Value ||
                                            vitals.circumference.value ||
                                            vitals.circumference}{" "}
                                          {vitals.circumference.Unit ||
                                            vitals.circumference.unit ||
                                            "cm"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.circumference.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);

                                            if (isNaN(date.getTime()))
                                              return "no date";

                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.bmi && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          BMI
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.bmi.Value ||
                                            vitals.bmi.value ||
                                            vitals.bmi}{" "}
                                          {vitals.bmi.Unit ||
                                            vitals.bmi.unit ||
                                            "kg/m²"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.bmi.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);

                                            if (isNaN(date.getTime()))
                                              return "no date";

                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.OxygenSaturation && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Oxygen Saturation
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.OxygenSaturation.Value ||
                                            vitals.OxygenSaturation.value ||
                                            vitals.OxygenSaturation}{" "}
                                          {vitals.OxygenSaturation.Unit ||
                                            vitals.OxygenSaturation.unit ||
                                            "%"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.OxygenSaturation
                                                .recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);
                                            if (isNaN(date.getTime()))
                                              return "no date";
                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.PainScore && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          NEWS Pain Score
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.PainScore.Value ||
                                            vitals.PainScore.value ||
                                            vitals.PainScore}{" "}
                                          {vitals.PainScore.Unit ||
                                            vitals.PainScore.unit ||
                                            ""}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.PainScore.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);
                                            if (isNaN(date.getTime()))
                                              return "no date";
                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.GlucoseLevel && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Blood Sugar
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.GlucoseLevel.Value ||
                                            vitals.GlucoseLevel.value ||
                                            vitals.GlucoseLevel}{" "}
                                          {vitals.GlucoseLevel.Unit ||
                                            vitals.GlucoseLevel.unit ||
                                            "mmol/L"}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.GlucoseLevel.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);
                                            if (isNaN(date.getTime()))
                                              return "no date";
                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.Consciousness && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Consciousness (AVPU)
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.Consciousness.Value ||
                                            vitals.Consciousness.value ||
                                            vitals.Consciousness}{" "}
                                          {vitals.Consciousness.Unit ||
                                            vitals.Consciousness.unit ||
                                            ""}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.Consciousness.recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);
                                            if (isNaN(date.getTime()))
                                              return "no date";
                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                    {vitals.SupplementalOxygen && (
                                      <tr>
                                        <td className="p-2 border border-blue-500">
                                          Supplemental Oxygen
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {vitals.SupplementalOxygen.Value ||
                                            vitals.SupplementalOxygen.value ||
                                            vitals.SupplementalOxygen}{" "}
                                          {vitals.SupplementalOxygen.Unit ||
                                            vitals.SupplementalOxygen.unit ||
                                            ""}
                                        </td>
                                        <td className="p-2 border border-blue-500">
                                          {(() => {
                                            const dateStr =
                                              vitals.SupplementalOxygen
                                                .recordedAt;
                                            if (!dateStr) return "No date";
                                            const date = new Date(dateStr);
                                            if (isNaN(date.getTime()))
                                              return "no date";
                                            return date.toLocaleString(
                                              "en-GB",
                                              {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              },
                                            );
                                          })()}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                              <div className="mt-6 text-sm text-gray-900">
                                <p>
                                  <strong>Vitals recorded by:</strong>
                                </p>
                                <p>{pharmacistName}</p>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={action.id}
                            className={`w-full p-4 rounded-lg border-l-4 ${
                              isDarkMode
                                ? "bg-gray-700 border-blue-500"
                                : "bg-gray-50 border-blue-600"
                            }`}
                          >
                            <div className="flex items-start gap-2 mb-2">
                              {getActionIcon(action.type)}
                              <h3
                                className={`font-semibold text-sm ${
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {action.type.charAt(0).toUpperCase() +
                                  action.type.slice(1)}
                              </h3>
                            </div>
                            <p
                              className={`text-sm mb-1 ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              <strong>Description:</strong> {action.description}
                            </p>
                            {action.id === "referral-consolidated" &&
                            patientReferalLetter?.referalLetter &&
                            Array.isArray(
                              patientReferalLetter.referalLetter,
                            ) ? (
                              <div className="mt-4 space-y-4">
                                {patientReferalLetter.referalLetter.map(
                                  (pdfBase64, index) => {
                                    const cachedImages =
                                      pdfImageCache[pdfBase64];
                                    if (
                                      cachedImages &&
                                      Array.isArray(cachedImages)
                                    ) {
                                      return cachedImages.map((src, i) => (
                                        <img
                                          key={`${index}-${i}`}
                                          src={src}
                                          className="w-full h-auto border rounded shadow-sm"
                                          alt={`Referral Letter ${index + 1} Page ${i + 1}`}
                                        />
                                      ));
                                    }
                                    return (
                                      <div
                                        key={index}
                                        className="w-full overflow-hidden bg-white border rounded shadow-sm"
                                      >
                                        <PdfImage
                                          base64={pdfBase64}
                                          fileType=".pdf"
                                          className="w-full h-auto"
                                          onConverted={(imageUrls) => {
                                            if (
                                              pdfBase64 &&
                                              !pdfImageCache[pdfBase64]
                                            ) {
                                              setPdfImageCache((prev) => ({
                                                ...prev,
                                                [pdfBase64]: imageUrls,
                                              }));
                                            }
                                          }}
                                        />
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            ) : action.id ===
                                "consultation-documents-consolidated" &&
                              action.data?.documents ? (
                              <div className="mt-4 space-y-6">
                                {action.data.documents.map(
                                  (doc: any, index: number) => (
                                    <div
                                      key={`prev-doc-${index}`}
                                      className="w-full"
                                    >
                                      <p
                                        className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                      >
                                        {doc.title}
                                      </p>
                                      {doc.fileContent && (
                                        <div className="w-full overflow-hidden bg-white border rounded shadow-sm">
                                          {pdfImageCache[doc.fileContent] &&
                                          Array.isArray(
                                            pdfImageCache[doc.fileContent],
                                          ) ? (
                                            <div className="space-y-4">
                                              {pdfImageCache[
                                                doc.fileContent
                                              ].map((src, i) => (
                                                <img
                                                  key={i}
                                                  src={src}
                                                  className="w-full h-auto"
                                                />
                                              ))}
                                            </div>
                                          ) : (
                                            <PdfImage
                                              base64={doc.fileContent}
                                              fileType={doc.fileType}
                                              className="w-full h-auto"
                                              onConverted={(imageUrls) => {
                                                if (
                                                  doc.fileContent &&
                                                  !pdfImageCache[
                                                    doc.fileContent
                                                  ]
                                                ) {
                                                  setPdfImageCache((prev) => ({
                                                    ...prev,
                                                    [doc.fileContent]:
                                                      imageUrls,
                                                  }));
                                                }
                                              }}
                                            />
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            ) : (
                              (action.details || action.data?.pdfContent) && (
                                <div className="mt-4">
                                  {isBase64ImageData(action.details) ? (
                                    <div className="w-full">
                                      <img
                                        src={getImageUrl(action.details)}
                                        alt="Action content"
                                        className="w-full h-auto border rounded shadow-sm"
                                      />
                                    </div>
                                  ) : isBase64ImageData(
                                      action.data?.pdfContent,
                                    ) ? (
                                    <div className="w-full">
                                      <img
                                        src={getImageUrl(
                                          action.data.pdfContent,
                                        )}
                                        alt="Document content"
                                        className="w-full h-auto border rounded shadow-sm"
                                      />
                                    </div>
                                  ) : isBase64PdfData(
                                      action.data?.pdfContent,
                                    ) || isBase64PdfData(action.details) ? (
                                    <div className="w-full overflow-hidden bg-white border rounded shadow-sm">
                                      {pdfImageCache[
                                        action.data?.pdfContent ||
                                          action.details
                                      ] &&
                                      Array.isArray(
                                        pdfImageCache[
                                          action.data?.pdfContent ||
                                            action.details
                                        ],
                                      ) ? (
                                        <div className="space-y-4">
                                          {pdfImageCache[
                                            action.data?.pdfContent ||
                                              action.details
                                          ].map((src, index) => (
                                            <img
                                              key={index}
                                              src={src}
                                              className="w-full h-auto"
                                            />
                                          ))}
                                        </div>
                                      ) : (
                                        <PdfImage
                                          base64={
                                            action.data?.pdfContent &&
                                            isBase64PdfData(
                                              action.data.pdfContent,
                                            )
                                              ? action.data.pdfContent
                                              : action.details
                                          }
                                          fileType=".pdf"
                                          className="w-full h-auto"
                                          onConverted={(imageUrls) => {
                                            const content =
                                              action.data?.pdfContent ||
                                              action.details;
                                            if (
                                              content &&
                                              !pdfImageCache[content]
                                            ) {
                                              setPdfImageCache((prev) => ({
                                                ...prev,
                                                [content]: imageUrls,
                                              }));
                                            }
                                          }}
                                        />
                                      )}
                                    </div>
                                  ) : isBase64TextData(
                                      action.data?.pdfContent || action.details,
                                    ) ? (
                                    <div className="w-full p-4 overflow-y-auto font-mono text-xs text-gray-800 whitespace-pre-wrap bg-white border rounded shadow-sm max-h-60">
                                      {decodeBase64Text(
                                        action.data?.pdfContent ||
                                          action.details,
                                      )}
                                    </div>
                                  ) : (
                                    <p
                                      className={`text-sm mb-1 ${
                                        isDarkMode
                                          ? "text-gray-300"
                                          : "text-gray-700"
                                      }`}
                                    >
                                      <strong>Details:</strong> {action.details}
                                    </p>
                                  )}
                                </div>
                              )
                            )}
                            <p
                              className={`text-xs ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              <strong>Completed:</strong>{" "}
                              {new Date(action.timestamp).toLocaleString(
                                "en-GB",
                              )}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Signatures */}
                <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Patient Signature */}
                  {effectivePatientSignature && (
                    <div
                      className={`p-4 rounded-lg border ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <h3
                        className={`font-semibold mb-3 text-sm ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Patient Consent & Signature
                      </h3>
                      <p
                        className={`text-xs mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <strong>Consent:</strong>{" "}
                        {effectivePatientSignature.consentText}
                      </p>
                      <p
                        className={`text-xs mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <strong>Signed by:</strong>{" "}
                        {effectivePatientSignature.patientName}
                      </p>
                      <p
                        className={`text-xs mb-3 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <strong>Date & Time:</strong>{" "}
                        {new Date(
                          effectivePatientSignature.timestamp,
                        ).toLocaleString("en-GB")}
                      </p>
                      <div className="flex items-center justify-center p-3 bg-white border rounded">
                        <img
                          src={effectivePatientSignature.signature}
                          alt="Patient Signature"
                          className="object-contain h-16 max-w-full"
                        />
                      </div>
                    </div>
                  )}

                  {/* Pharmacist Signature */}
                  {effectivePharmacistSignature ? (
                    <div
                      className={`p-4 rounded-lg border ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <h3
                        className={`font-semibold mb-3 text-sm ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Pharmacist Signature
                      </h3>
                      <p
                        className={`text-xs mb-2 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <strong>Pharmacist:</strong>{" "}
                        {effectivePharmacistSignature.patientName}
                      </p>
                      <p
                        className={`text-xs mb-3 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <strong>Date & Time:</strong>{" "}
                        {new Date(
                          effectivePharmacistSignature.timestamp,
                        ).toLocaleString("en-GB")}
                      </p>
                      <div className="flex items-center justify-center p-3 bg-white border rounded">
                        <img
                          src={effectivePharmacistSignature.signature}
                          alt="Pharmacist Signature"
                          className="object-contain h-16 max-w-full"
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`text-center py-4 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      <p className="text-sm">
                        No pharmacist signature captured
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div
                  className={`text-xs pt-4 border-t ${
                    isDarkMode
                      ? "border-gray-700 text-gray-400"
                      : "border-gray-200 text-gray-600"
                  }`}
                >
                  <p>
                    This document was generated electronically on{" "}
                    {new Date().toLocaleString("en-GB")}
                  </p>
                  <p className="mt-1">
                    © {new Date().getFullYear()} Pharmacy Management System
                  </p>
                </div>
              </div>
            </div>

            {/* Preview Footer */}
            <div
              className={`flex-shrink-0 p-4 border-t ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              } flex justify-between items-center`}
            >
              <div
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {!isDocumentGenerated ? (
                  <span>
                    Preview shows {selectedActions.length} action
                    {selectedActions.length !== 1 ? "s" : ""}
                    {isAiSummarySelected ? " + Summary" : ""}
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    Document Generated
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {!isDocumentGenerated ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPreview(false)}
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Back
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleGenerateAndSaveDocument}
                      disabled={isGeneratingDocument}
                      className="text-white bg-black hover:bg-gray-900"
                    >
                      {isGeneratingDocument ? (
                        <>
                          <FileText className="w-4 h-4 mr-2 " />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Generate Document
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowPreview(false);
                        onClose();
                      }}
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Back
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEmailDialog(true)}
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrint}
                      className={`${
                        isDarkMode
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Print
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
